import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import {
  BleClient,
  ScanResult,
  numberToUUID,
  dataViewToText,
  textToDataView,
  dataViewToNumbers,
  ScanMode,
} from "@capacitor-community/bluetooth-le";
import { useHistory, useLocation } from "react-router-dom"; // Import useHistory
import { Storage } from "@ionic/storage";
import {
  convertToRealtimeData,
  createLogStringFromByteArray,
  decodeAndValidatePayload,
  decodeCurveData,
  decodeMeasurementBitmask,
  decodeUpdatedCurvesList,
  decodeUpdatedTitrationSummary,
  generateMessageTypeRequest,
  mergeChunks,
  parseAdherenceFromText,
  parseAndLogChunk,
  parseBLETitrationData,
} from "../utils/bleLogDataParseHelper";
import { sendAllLogs } from "../utils/bleLogDataPushHelper";
import { isPlatform, useIonRouter } from "@ionic/react";
import {
  getTherapies,
  initStorage,
  loadDeviceFromStorage,
  saveDeviceToStorage,
  BatchMetadata,
  getBatchesMetadata,
  clearTherapyData,
  storeAdherenceData,
  storeCurveAnalysisSession,
  getLastFetchedAdherence,
  getAllCurveAnalysisSessions,
  getCurveAnalysisSessionById,
  clearCurveAnalysisData,
  clearAdherenceData,
  clearAllTitrationSessions,
  storeTitrationSession,
  getTitrationSessionByRefId,
  getAllTitrationSessions,
  savePulseOximeterToStorage,
  loadPulseOximeterFromStorage,
} from "../services/storageService";
import { Device, Device as LocalDevice } from "@capacitor/device";
import {
  AdherenceData,
  BleError,
  ChunkHeader,
  ChunkPosition,
  CurveAnalysisData,
  DataPoint,
  DeviceType,
  MessageType,
  PLXMeasurement,
  RealtimeData,
  RequestMessageType,
  TherapyParameters,
  TherapyParamsPart1,
  TherapyParamsPart2,
  TherapyPhase,
  TherapySession,
  TitrationData,
  TitrationSession,
} from "./types";
import { App } from "@capacitor/app";
import ActionButtonsConnected from "../pages/remote/subcompoenents/ConnectionModal";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { parsePLXData } from "../utils/plxDataParser";
import { logUserAction } from "../services/errorHandler";
import analytics from "../services/firebaseAnalytics";
import { platform } from "os";
// import { mockAdherenceData } from "../pages/remote/subcompoenents/AdheranceSummary";
// import { mockEnhancedTitrationData } from "../pages/remote/subcompoenents/TitrationSummary";
// import { mockCurveData } from "../pages/remote/subcompoenents/CurveAnalysis";

interface BLEContextProps {
  isConnected: boolean;
  device: ScanResult | null; // Currently connected device
  devices: ScanResult[]; // List of discovered devices
  newDevice: ScanResult | null; // Track the latest discovered device for the popup
  setConnectionStatus: (status: boolean, device?: ScanResult) => void;
  connectToDevice: (
    device: ScanResult,
    disConnectFromDevice?: string
  ) => Promise<{ status: number; message: string }>; // Connect to a device
  connectToPLXDeviceSimple: (
    device: ScanResult
  ) => Promise<{ status: number; message: string }>; // Connect to a PLX device
  dismissDevicePopup: () => void; // Dismiss the popup
  showNextDevice: () => void;
  showNewDeviceModal: boolean;
  disConnectFromDevice: (deviceId: string) => Promise<void>;
  deviceType: string; // Type of the device (e.g., "Cough")
  serialNumber: string;
  ribbonState: boolean;
  BLELogCount: number;
  serverSuccessLogCount: number;
  serverFailLogCount: number;
  batchesMetaData: BatchMetadata[];
  mainMap: string;
  valMap: string;
  batchCount: number;
  heartRateData: string;
  mode: string;
  scanningStatus: "scanning" | "paused" | "stopped";
  therapyState: boolean;
  setTherapyState: (e: boolean) => void;
  time: number;
  setTime: (e: number) => void;
  storage: Storage | null;
  HEART_RATE_SERVICE: string;
  HEART_RATE_MEASUREMENT_CHARACTERISTIC: string;
  recentLog: { batch_id: string; therapy: string };
  showSplash: boolean;
  currentPhase: string;
  phaseProgress: number;
  therapyParams: TherapyParameters | null;
  openModal: (
    action:
      | "connect"
      | "connectFailed"
      | "disconnect"
      | "connected"
      | "closeDeviceOverlay"
      | "notConnectedToDevice"
      | "TherapyNotFound"
      | "curveAnalysisIncomplete"
      | "pulseDeviceDisconnect"
      | "connectedPulseOximeter",
    deviceData: ScanResult,
    message?: string
  ) => void; // Open the modal
  closeModal: () => void; // Close the modal
  handleModalAction: () => Promise<void>; // Handle modal actions
  toggleTherapyState: () => Promise<void>; // Toggle therapy state
  handleInspPhaseStart: () => Promise<void>; // Handle inspiratory phase start
  handleInspPhaseStop: () => Promise<void>; // Handle inspiratory phase release
  handleExpPhaseStart: () => Promise<void>; // Handle expiratory phase start
  handleExpPhaseStop: () => Promise<void>; // Handle expiratory phase release
  gaugeValue: number; // Gauge value for therapy progress
  manualPauseTime: number; // Manual pause time for therapy
  manualInspTime: number; // Manual inspiratory time for therapy
  manualExpTime: number; // Manual expiratory time for therapy
  manualCycleNumber: number; // Manual cycle number for therapy
  currentProfileName: string; // Current profile name for therapy
  handleFindDevice: () => void; // Function to find a device
  handleSyncAdherenceSummary: (dateRange: {
    start: string;
    end: string;
    tz: string;
  }) => Promise<void>; // Function to sync adherence summary
  adherenceData: AdherenceData | null; // Adherence data
  adherenceLoading: boolean; // Loading state for adherence data
  adherenceBackgroundLoading: boolean; // Background loading state for adherence data
  titrationData: TitrationData | null; // Titration data
  titrationLoading: boolean; // Loading state for titration data
  handleSyncTitration: (session: TitrationSession) => Promise<void>; // Function to sync titration data
  curveData: CurveAnalysisData | null; // Curve analysis data
  curveLoading: boolean; // Loading state for curve analysis data
  handleSyncCurveAnalysis: (
    session: TherapySession,
    pressure: boolean,
    flow: boolean,
    volume: boolean
  ) => Promise<void>; // Function to sync curve analysis data
  curvesList: TherapySession[]; // List of therapy sessions
  adherenceProgress: number; // Progress for adherence data
  curvesProgress: number; // Progress for curve analysis data
  titrationProgress: number; // Progress for titration data
  titrationTherapiesList: TitrationSession[]; // List of titration therapies
  storedAdherenceDeviceId: string; // Device ID for stored adherence data
  storedTitrationDeviceId: string; // Device ID for stored titration data
  storedCurveDataDeviceId: string; // Device ID for stored curve data
  realtimeData: RealtimeData; // Real-time data for therapy
  realtimeDataPoints: RealtimeData; // Real-time data points for therapy
  askForRealTimeCurves: (
    pressure: boolean,
    flow: boolean,
    volume: boolean,
    intervalMs?: number
  ) => Promise<void>; // Function to request real-time curves
  askForPressureGaugeData: () => Promise<void>; // Function to request pressure gauge data
  parseDeviceString: (deviceString: string) => {
    deviceType: string;
    serialNumber: string;
  };
  askForSelectiveData: (requestTypes: RequestMessageType[]) => Promise<void>; // Function to request selective data
  setCurveData: (data: CurveAnalysisData | null) => void; // Function to set curve data
  deviceVersion: string; // Device version
  isConnectClicked: { device: ScanResult | null; status: boolean }; // Track connection click status
  isPulseOximeterConnectClicked: { device: ScanResult | null; status: boolean };
  isTherapyRunning: boolean; // Track if therapy is currently running
  isPulseOximeterConnected: boolean;
  pulseOximeterDevice: ScanResult | null;
  pulseOximeterDevices: ScanResult[];
  pulseOximeterData: PLXMeasurement | null;
  setPulseOximeterConnectionStatus: (
    status: boolean,
    device?: ScanResult | null
  ) => void;
  connectionStatus: {
    isConnected: boolean;
    therapyConnected: boolean;
    plxConnected: boolean;
    deviceCount: number;
    statusText: string;
  };
  // Add device type utilities for UI
  DeviceType: typeof DeviceType;
  getDeviceType: (deviceName: string) => DeviceType;
  // Add these new functions for Pulse Oximeter Connection management
  connectToPulseOximeterDevice: (
    device: ScanResult,
    deviceToDisconnectFrom?: string
  ) => Promise<{ status: number; message: string }>;
  disconnectFromPulseOximeterDevice: (deviceId: string) => Promise<void>;
  handlePLXDataReceived: (measurement: PLXMeasurement) => void;
  pulseOximeterSessionData: {
    spO2Data: DataPoint[];
    pulseRateData: DataPoint[];
  };
  pulseOximeterSessionDataPoint: {
    spO2Data: DataPoint | null;
    pulseRateData: DataPoint | null;
  };
  findAndConnectToDeviceWeb: () => Promise<void>;
}

interface BLEProviderProps {
  children: ReactNode;
}

export const BLEContext = createContext<BLEContextProps | undefined>(undefined);

export const BLEProvider: React.FC<BLEProviderProps> = ({ children }) => {
  const [localDeviceName, setLocalDeviceName] = useState("");
  const [isBleInitialized, setIsBleInitialized] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnectClicked, setIsConnectClicked] = useState<{
    device: ScanResult | null;
    status: boolean;
  }>({ device: null, status: false });
  const isConnectClickedRef = useRef<{
    device: ScanResult | null;
    status: boolean;
  }>({ device: null, status: false });
  const [device, setDevice] = useState<ScanResult | null>(null);
  const [devices, setDevices] = useState<ScanResult[]>([]);
  const [newDeviceIndex, setNewDeviceIndex] = useState<number>(0); // Default to the first device
  const [showNewDeviceModal, setShowNewDeviceModal] = useState(false);
  // Add after existing state declarations
  const [isPulseOximeterConnected, setIsPulseOximeterConnected] =
    useState(false);
  const [pulseOximeterDevice, setPulseOximeterDevice] =
    useState<ScanResult | null>(null);
  const [pulseOximeterDevices, setPulseOximeterDevices] = useState<
    ScanResult[]
  >([]);
  const [isPulseOximeterConnectClicked, setIsPulseOximeterConnectClicked] =
    useState<{
      device: ScanResult | null;
      status: boolean;
    }>({ device: null, status: false });
  const [pulseOximeterData, setPulseOximeterData] =
    useState<PLXMeasurement | null>(null);
  const [pulseOximeterSessionData, setPulseOximeterSessionData] = useState<{
    spO2Data: DataPoint[];
    pulseRateData: DataPoint[];
  }>({
    spO2Data: [],
    pulseRateData: [],
  });
  const [pulseOximeterSessionDataPoint, setPulseOximeterSessionDataPoint] =
    useState<{
      spO2Data: DataPoint | null;
      pulseRateData: DataPoint | null;
    }>({
      spO2Data: null,
      pulseRateData: null,
    });
  const therapySessionStartTimeRef = useRef<number | null>(null);
  const [showSplash, setShowSplash] = useState(true);
  const history = useHistory(); // Hook for navigation
  const location = useLocation();
  const [storage, setStorage] = useState<Storage | null>(null);
  const HEART_RATE_SERVICE = numberToUUID(0x180d);
  const GLUCOSE_SERVICE = numberToUUID(0x1808);
  const PULSE_OXIMETER_SERVICE = numberToUUID(0x1822);
  const HEART_RATE_MEASUREMENT_CHARACTERISTIC = numberToUUID(0x2a37);
  const GLUCOSE_MEASUREMENT_CHARACTERISTIC = numberToUUID(0x2a18);
  const [mode, setMode] = useState<"Auto" | "Manual" | "default">("Manual");
  const [ribbonState, setRibbonState] = useState(false);
  const [therapyState, setTherapyState] = useState(false);
  const [isTherapyRunning, setIsTherapyRunning] = useState(false); // for realtime graphs since therapystate is false before therapy complete
  const isTherapyRunningRef = useRef(false);
  const [time, setTime] = useState(0);
  const [deviceType, setDeviceType] = useState<string>("Cough");
  const [BLELogCount, setBLELogCount] = useState<number>(0);
  const [serverSuccessLogCount, setServerSuccessLogCount] = useState<number>(0);
  const [serverFailLogCount, setServerFailLogCount] = useState<number>(0);
  const [batchesMetaData, setBatchesMetaData] = useState<BatchMetadata[]>([]);
  const [batchCount, setBatchCount] = useState<number>(0);
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [deviceVersion, setDeviceVersion] = useState<string>("");
  const [heartRateData, setHeartRatedata] = useState<string>("");
  const lastDeviceManuallyDisconnectedFromRef = useRef<string | null>(null);
  const lastPulseOximeterManuallyDisconnectedFromRef = useRef<string | null>(
    null
  );
  const [therapyParams, setTherapyParams] = useState<TherapyParameters | null>(
    null
  );
  const [currentPhase, setCurrentPhase] = useState(TherapyPhase.INACTIVE);
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [gaugeValue, setGaugeValue] = useState(0);
  const [manualPauseTime, setManualPauseTime] = useState(0);
  const [manualInspTime, setManualInspTime] = useState(0);
  const [manualExpTime, setManualExpTime] = useState(0);
  const [manualCycleNumber, setManualCycleNumber] = useState(0);
  const [currentProfileName, setCurrentProfileName] = useState<string>("");
  const [mainMap, setMainMap] = useState("");
  const [valMap, setValMap] = useState("");
  const [recentLog, setRecentLog] = useState<{
    batch_id: string;
    therapy: string;
  }>({ batch_id: "", therapy: "" });
  const [hiddenDisconnect, setHiddenDisconnect] = useState(false);
  const router = useIonRouter();
  const isConnectedRef = useRef(false);
  const deviceIdRef = useRef<string | null>(null);
  const deviceNameRef = useRef<string | null>(null);
  const realtimeDataCountRef = useRef<number | null>(null);
  const realtimeDataRef = useRef<RealtimeData | null>(null);
  const sendBuffTimestampRef = useRef(Date.now());
  const storageRef = useRef<Storage | null>(null);
  const [scanningStatus, setScanningStatus] = useState<
    "scanning" | "paused" | "stopped"
  >("stopped");
  // Add refs to store timeout IDs for cleanup
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentCurveSessionRef = useRef<TherapySession | null>(null);
  const currentTitrationSessionRef = useRef<TitrationSession | null>(null);

  // Timeout refs for each data type
  const adherenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const titrationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const curvesTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Queue management variables
  let isProcessingPushToServerQueue = false;
  let queueTimeout: NodeJS.Timeout | null = null;

  const getDeviceType = (deviceName: string): DeviceType => {
    if (!deviceName) return DeviceType.UNKNOWN;
    if (deviceName.startsWith("ABM")) return DeviceType.THERAPY;
    if (deviceName.startsWith("Non")) return DeviceType.PULSE_OXIMETER;
    return DeviceType.UNKNOWN;
  };

  // Add unified connection status getter (read-only)
  const getConnectionStatus = useCallback(() => {
    const therapyConnected = isConnected;
    const plxConnected = isPulseOximeterConnected;

    return {
      isConnected: therapyConnected || plxConnected, // backward compatibility
      therapyConnected,
      plxConnected,
      deviceCount: (therapyConnected ? 1 : 0) + (plxConnected ? 1 : 0),
      statusText:
        therapyConnected && plxConnected
          ? "Connected to 2 devices"
          : therapyConnected
          ? "Connected to BiWaze® System"
          : plxConnected
          ? "Connected to Pulse Oximeter"
          : "Not Connected",
    };
  }, [isConnected, isPulseOximeterConnected]);

  // Start therapy session timer when therapy begins
  const startTherapySessionTimer = useCallback(() => {
    therapySessionStartTimeRef.current = Date.now();
    console.log("Therapy session timer started");
  }, []);

  // Get elapsed time since therapy session started
  const getTherapyElapsedTime = useCallback((): number => {
    if (!therapySessionStartTimeRef.current) {
      return 0;
    }
    return Date.now() - therapySessionStartTimeRef.current;
  }, []);

  // Reset therapy session data when therapy stops
  const resetTherapySessionData = useCallback(() => {
    setPulseOximeterSessionData({
      spO2Data: [],
      pulseRateData: [],
    });
    setPulseOximeterSessionDataPoint({
      spO2Data: null,
      pulseRateData: null,
    });
    therapySessionStartTimeRef.current = null;
    console.log("Therapy session PLX data reset");
  }, []);

  // Function to process the queue that sends logs to the server
  async function processQueue() {
    if (isProcessingPushToServerQueue || logStringList.length === 0) {
      return;
    }

    isProcessingPushToServerQueue = true;

    try {
      // Clear timeout since we're processing now
      if (queueTimeout) {
        clearTimeout(queueTimeout);
        queueTimeout = null;
      }

      // Take up to 10 logs from the queue
      const logsToSend = logStringList.splice(0, 10);
      // console.log(`Processing ${logsToSend.length} logs from queue`);

      // Send logs using your existing helper
      const { successCount, failureCount, statusMessages } = await sendAllLogs(
        logsToSend
      );

      // Update counters as per your existing logic
      setServerSuccessLogCount((prevCount) => prevCount + successCount);
      setServerFailLogCount((prevCount) => prevCount + failureCount);
      setBatchCount((prevCount) => prevCount + 1);

      if (deviceIdRef.current && isConnectedRef.current)
        addToServerStatusQueue(statusMessages, deviceIdRef.current);

      // console.log(
      //   `Queue processed: ${successCount} success, ${failureCount} failed`
      // );
    } catch (error) {
      console.error("Error processing log queue:", error);
    } finally {
      isProcessingPushToServerQueue = false;

      // If more logs remain, schedule next processing
      if (logStringList.length > 0) {
        scheduleProcessing();
      }
    }
  }

  // Function to schedule processing with timeout
  function scheduleProcessing() {
    if (queueTimeout) {
      clearTimeout(queueTimeout);
    }

    queueTimeout = setTimeout(() => {
      processQueue();
    }, 5000); // 5 second timeout
  }

  // modal related state variables
  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    action: null as
      | "connect"
      | "connectFailed"
      | "disconnect"
      | "connected"
      | "closeDeviceOverlay"
      | "notConnectedToDevice"
      | "curveAnalysisIncomplete"
      | "TherapyNotFound"
      | "pulseDeviceDisconnect"
      | "connectedPulseOximeter"
      | null,
    device: null as ScanResult | null,
    isLoading: false,
    message: undefined as string | undefined,
  });
  const openModal = (
    action:
      | "connect"
      | "connectFailed"
      | "disconnect"
      | "connected"
      | "closeDeviceOverlay"
      | "notConnectedToDevice"
      | "curveAnalysisIncomplete"
      | "TherapyNotFound"
      | "pulseDeviceDisconnect"
      | "connectedPulseOximeter",
    deviceData: ScanResult | null,
    message?: string
  ) => {
    setModalConfig({
      isOpen: true,
      action,
      device: deviceData,
      isLoading: false,
      message: message || "",
    });
  };

  const closeModal = () => {
    // console.log("Closing modal");
    setTimeout(() => {
      setModalConfig({
        isOpen: false,
        action: null,
        device: null,
        isLoading: false,
        message: undefined,
      });
    }, 100); // Delay to allow modal to close smoothly
  };
  // Adherence Summary state
  const [adherenceData, setAdherenceData] = useState<AdherenceData | null>(
    null
  );
  const [adherenceLoading, setAdherenceLoading] = useState(false);
  const [adherenceBackgroundLoading, setAdherenceBackgroundLoading] =
    useState(false);
  // Titration Summary state
  const [titrationData, setTitrationData] = useState<TitrationData | null>(
    null
  );
  const [titrationLoading, setTitrationLoading] = useState(false);
  const [titrationTherapiesList, setTitrationTherapiesList] = useState<
    TitrationSession[]
  >([]);
  // Therapy curves state
  const [curveData, setCurveData] = useState<CurveAnalysisData | null>(null);
  const [curveLoading, setCurveLoading] = useState(false);
  const [curvesList, setCurvesList] = useState<TherapySession[]>([]);
  const [adherenceProgress, setAdherenceProgress] = useState(0);
  const [curvesProgress, setCurvesProgress] = useState(0);
  const [titrationProgress, setTitrationProgress] = useState(0);
  const [storedAdherenceDeviceId, setStoredAdherenceDeviceId] = useState("");
  const [storedTitrationDeviceId, setStoredTitrationDeviceId] = useState("");
  const [storedCurveDataDeviceId, setStoredCurveDataDeviceId] = useState("");
  // Realtime Curves state full data
  const [realtimeData, setRealtimeData] = useState<RealtimeData>({
    flowData: [],
    pressureData: [],
    volumeData: [],
  });
  // Realtime Data Points state only new points
  const [realtimeDataPoints, setRealtimeDataPoints] = useState<RealtimeData>({
    flowData: [],
    pressureData: [],
    volumeData: [],
  });

  // Helper to get timeout ref for data type
  const getTimeoutRef = (dataType: "adherence" | "titration" | "curves") => {
    switch (dataType) {
      case "adherence":
        return adherenceTimeoutRef;
      case "titration":
        return titrationTimeoutRef;
      case "curves":
        return curvesTimeoutRef;
    }
  };

  const startInsightsProgressTimer = useCallback(
    (
      dataType: "adherence" | "titration" | "curves",
      onTimeout?: () => void
    ) => {
      const timeoutRef = getTimeoutRef(dataType);

      // Clear any existing timer first
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Start new 5-second timer
      timeoutRef.current = setTimeout(() => {
        // console.log(`${dataType} transfer timed out - no chunks received`);

        // Clear the timeout ref
        timeoutRef.current = null;

        // Call your custom timeout handler if provided
        if (onTimeout) {
          onTimeout();
        }
      }, 5000);

      // console.log(`Started ${dataType} timer`);
    },
    []
  );

  const resetInsightsProgressTimer = useCallback(
    (
      dataType: "adherence" | "titration" | "curves",
      onTimeout?: () => void
    ) => {
      const timeoutRef = getTimeoutRef(dataType);

      // Clear existing timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Start fresh 5-second timer
      timeoutRef.current = setTimeout(() => {
        // console.log(`${dataType} transfer timed out - no new chunks received`);

        // Clear the timeout ref
        timeoutRef.current = null;

        // Call your custom timeout handler if provided
        if (onTimeout) {
          onTimeout();
        }
      }, 5000);

      // console.log(`Reset ${dataType} timer - chunk received`);
    },
    []
  );

  const stopInsightsProgressTimer = useCallback(
    (dataType: "adherence" | "titration" | "curves") => {
      const timeoutRef = getTimeoutRef(dataType);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        // console.log(`Stopped ${dataType} timer - transfer completed`);
      }
    },
    []
  );

  const stopAllTimers = useCallback(() => {
    stopInsightsProgressTimer("adherence");
    stopInsightsProgressTimer("titration");
    stopInsightsProgressTimer("curves");
    // console.log("Stopped all timers");
  }, [stopInsightsProgressTimer]);

  const cleanup = useCallback(() => {
    if (adherenceTimeoutRef.current) {
      clearTimeout(adherenceTimeoutRef.current);
      adherenceTimeoutRef.current = null;
    }
    if (titrationTimeoutRef.current) {
      clearTimeout(titrationTimeoutRef.current);
      titrationTimeoutRef.current = null;
    }
    if (curvesTimeoutRef.current) {
      clearTimeout(curvesTimeoutRef.current);
      curvesTimeoutRef.current = null;
    }
    // console.log("Cleaned up all BLE timers");
  }, []);

  // Check if a timer is active for a data type
  // const isTimerActive = useCallback(
  //   (dataType: "adherence" | "titration" | "curves"): boolean => {
  //     const timeoutRef = getTimeoutRef(dataType);
  //     return timeoutRef.current !== null;
  //   },
  //   []
  // );

  // // Get status of all timers
  // const getTimerStatus = useCallback(() => {
  //   return {
  //     adherence: isTimerActive("adherence"),
  //     titration: isTimerActive("titration"),
  //     curves: isTimerActive("curves"),
  //   };
  // }, [isTimerActive]);

  // Read Device Information Service (0x180A)
  const readDeviceInformation = async (deviceId: string) => {
    console.log("\n📱 READING DEVICE INFORMATION:");

    const deviceInfoService = "0000180a-0000-1000-8000-00805f9b34fb";

    // Characteristic UUIDs
    const characteristics = {
      manufacturerName: "00002a29-0000-1000-8000-00805f9b34fb",
      modelNumber: "00002a24-0000-1000-8000-00805f9b34fb",
      serialNumber: "00002a25-0000-1000-8000-00805f9b34fb",
      hardwareRevision: "00002a27-0000-1000-8000-00805f9b34fb",
      firmwareRevision: "00002a26-0000-1000-8000-00805f9b34fb",
      softwareRevision: "00002a28-0000-1000-8000-00805f9b34fb",
      systemId: "00002a23-0000-1000-8000-00805f9b34fb",
      regulatoryCertification: "00002a2a-0000-1000-8000-00805f9b34fb",
    };

    // Helper to convert DataView to string
    const dataViewToString = (dataView: DataView): string => {
      const decoder = new TextDecoder("utf-8");
      return decoder.decode(dataView.buffer);
    };

    // Helper to convert DataView to hex string
    const dataViewToHex = (dataView: DataView): string => {
      const hexArray = [];
      for (let i = 0; i < dataView.byteLength; i++) {
        hexArray.push(
          dataView.getUint8(i).toString(16).padStart(2, "0").toUpperCase()
        );
      }
      return hexArray.join(" ");
    };

    // Read Manufacturer Name
    try {
      const data = await BleClient.read(
        deviceId,
        deviceInfoService,
        characteristics.manufacturerName
      );
      const manufacturer = dataViewToString(data);
      console.log(`   👤 Manufacturer: ${manufacturer}`);
    } catch (error) {
      console.log(`   👤 Manufacturer: [Not available]`);
    }

    // Read Model Number
    try {
      const data = await BleClient.read(
        deviceId,
        deviceInfoService,
        characteristics.modelNumber
      );
      const model = dataViewToString(data);
      console.log(`   📦 Model Number: ${model}`);
    } catch (error) {
      console.log(`   📦 Model Number: [Not available]`);
    }

    // Read Serial Number
    try {
      const data = await BleClient.read(
        deviceId,
        deviceInfoService,
        characteristics.serialNumber
      );
      const serial = dataViewToString(data);
      console.log(`   🔢 Serial Number: ${serial}`);
    } catch (error) {
      console.log(`   🔢 Serial Number: [Not available]`);
    }

    // Read Hardware Revision
    try {
      const data = await BleClient.read(
        deviceId,
        deviceInfoService,
        characteristics.hardwareRevision
      );
      const hardware = dataViewToString(data);
      console.log(`   🔧 Hardware Revision: ${hardware}`);
    } catch (error) {
      console.log(`   🔧 Hardware Revision: [Not available]`);
    }

    // Read Firmware Revision
    try {
      const data = await BleClient.read(
        deviceId,
        deviceInfoService,
        characteristics.firmwareRevision
      );
      const firmware = dataViewToString(data);
      console.log(`   💾 Firmware Revision: ${firmware}`);
    } catch (error) {
      console.log(`   💾 Firmware Revision: [Not available]`);
    }

    // Read Software Revision
    try {
      const data = await BleClient.read(
        deviceId,
        deviceInfoService,
        characteristics.softwareRevision
      );
      const software = dataViewToString(data);
      console.log(`   💿 Software Revision: ${software}`);
    } catch (error) {
      console.log(`   💿 Software Revision: [Not available]`);
    }

    // Read System ID
    try {
      const data = await BleClient.read(
        deviceId,
        deviceInfoService,
        characteristics.systemId
      );
      const systemId = dataViewToHex(data);
      console.log(`   🆔 System ID: ${systemId}`);
    } catch (error) {
      console.log(`   🆔 System ID: [Not available]`);
    }

    // Read Regulatory Certification
    try {
      const data = await BleClient.read(
        deviceId,
        deviceInfoService,
        characteristics.regulatoryCertification
      );

      // Regulatory certification is a complex structure
      // First byte is the length of the list
      const listLength = data.getUint8(0);
      console.log(`   📜 Regulatory Certifications (${listLength} items):`);

      let offset = 1;
      for (let i = 0; i < listLength && offset < data.byteLength; i++) {
        // Each item has a body and length
        const bodyLength = data.getUint8(offset);
        offset += 1;

        if (offset + bodyLength <= data.byteLength) {
          const certData = new Uint8Array(data.buffer, offset, bodyLength);
          const certHex = Array.from(certData)
            .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
            .join(" ");
          console.log(`      Item ${i + 1}: ${certHex}`);
          offset += bodyLength;
        }
      }
    } catch (error) {
      console.log(`   📜 Regulatory Certification: [Not available]`);
    }

    console.log("   ========================================");
  };

  // Read Battery Service (0x180F)
  const readBatteryLevel = async (deviceId: string) => {
    console.log("\n🔋 READING BATTERY LEVEL:");

    const batteryService = "0000180f-0000-1000-8000-00805f9b34fb";
    const batteryLevelChar = "00002a19-0000-1000-8000-00805f9b34fb";

    try {
      const data = await BleClient.read(
        deviceId,
        batteryService,
        batteryLevelChar
      );

      // Battery level is a single byte (0-100)
      const batteryLevel = data.getUint8(0);

      // Determine battery status
      let status = "";
      let emoji = "";
      if (batteryLevel >= 80) {
        status = "Excellent";
        emoji = "🟢";
      } else if (batteryLevel >= 50) {
        status = "Good";
        emoji = "🟡";
      } else if (batteryLevel >= 20) {
        status = "Low";
        emoji = "🟠";
      } else {
        status = "Critical";
        emoji = "🔴";
      }

      console.log(`   ${emoji} Battery Level: ${batteryLevel}% (${status})`);

      // Visual battery bar
      const barLength = 20;
      const filledBars = Math.round((batteryLevel / 100) * barLength);
      const emptyBars = barLength - filledBars;
      const bar = "█".repeat(filledBars) + "░".repeat(emptyBars);
      console.log(`   [${bar}] ${batteryLevel}%`);

      // Warning if low
      if (batteryLevel < 20) {
        console.warn(`   ⚠️ WARNING: Battery is low! Replace batteries soon.`);
      }

      console.log("   ========================================");

      return batteryLevel;
    } catch (error) {
      console.log(`   Battery Level: [Not available]`);
      console.log("   ========================================");
      return null;
    }
  };

  // Function to request whatever data is needed
  const askForSelectiveData = async (
    requestTypes: RequestMessageType[],
    deviceParam?: ScanResult
  ) => {
    const connectedDevice = deviceParam || device;
    if (
      connectedDevice &&
      connectedDevice.device.deviceId &&
      requestTypes.length > 0
    ) {
      try {
        const msgString = generateMessageTypeRequest(requestTypes);
        console.log("Requesting data with message:", msgString);
        await BleClient.write(
          connectedDevice.device.deviceId,
          GLUCOSE_SERVICE,
          GLUCOSE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(msgString.apiString)
        );
      } catch (error) {
        console.error("Failed to request data:", error);
      }
    } else {
      console.warn("Not connected or device ID not set for data request");
    }
  };

  const handleModalAction = async (index?: number) => {
    if (!modalConfig.action) return;

    setModalConfig((prev) => ({ ...prev, isLoading: true }));

    try {
      if (modalConfig.action === "disconnect") {
        const deviceId =
          (modalConfig.device && modalConfig.device.device.deviceId) ||
          deviceIdRef.current ||
          "";
        await disConnectFromDevice(deviceId);
      }
      if (modalConfig.action === "pulseDeviceDisconnect") {
        const deviceId = pulseOximeterDevice?.device.deviceId || "";
        await disconnectFromPulseOximeterDevice(deviceId);
      }
      if (
        modalConfig.action === "connected" ||
        modalConfig.action === "connectedPulseOximeter"
      ) {
        router.push("/remote");
      } else {
        if (
          location.pathname === "/remote/deviceInfo" ||
          location.pathname === "/remote/pulseOximeterInfo"
        ) {
          router.push("/remote/scan");
        }
      }
      if (index === 0 && modalConfig.action === "curveAnalysisIncomplete") {
        // Continue with the existing session
        return;
      } else if (
        index === 1 &&
        modalConfig.action === "curveAnalysisIncomplete"
      ) {
        // Fetch fresh data from the device
        if (deviceIdRef.current && isConnectedRef.current) {
          await BleClient.write(
            deviceIdRef.current,
            GLUCOSE_SERVICE,
            GLUCOSE_MEASUREMENT_CHARACTERISTIC,
            textToDataView(
              `sendCurves,7,${currentCurveSessionRef.current?.refId}`
            )
          );
          setCurvesProgress(0);
          setCurveLoading(true);
          startInsightsProgressTimer("curves", () => {
            setCurveLoading(false);
            setCurveData(null);
            setCurvesProgress(0);
          });
        }
      }

      closeModal();
    } catch (error) {
      console.error(`Error ${modalConfig.action}ing device:`, error);
      setModalConfig((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const getModalMessages = () => {
    const deviceName =
      parseDeviceString(modalConfig.device?.device.name || "").deviceType ||
      "BiWaze® Device";

    switch (modalConfig.action) {
      case "connectFailed":
        return {
          Title: "Device Connection Failed",
          Message: modalConfig.message
            ? modalConfig.message
            : `Failed to connect to ${deviceName}. Please try again.`,
          ButtonText: modalConfig.message ? "Done" : "Retry",
        };
      case "connect":
        return {
          Title: "Connect Device",
          Message: `Would you like to connect to ${deviceName}?`,
          ButtonText: "Connect",
        };
      case "connected":
        return {
          Title: "Device Connected",
          Message: `Great! Your have successfully connected to ${deviceName}.`,
          ButtonText: "Done",
        };
      case "disconnect":
        return {
          Title: "Disconnect Device",
          Message: `Are you sure you want to disconnect from your device?`,
          ButtonText: "Disconnect",
        };
      case "closeDeviceOverlay":
        return {
          Title: "Device Overlay",
          Message: `Close The Overlay on the ${deviceName}`,
          ButtonText: "Done",
        };
      case "notConnectedToDevice":
        return {
          Title: "Device Not Connected",
          Message: `Ensure that you are connected to a BiWaze® device before proceeding.`,
          ButtonText: "Done",
        };
      case "curveAnalysisIncomplete":
        return {
          Title: "Real Time Curve Session Available",
          Message: `A session for this Therapy already exists on your device.\n
    Would you like to continue using it, or fetch a fresh copy from the device?`,
          ButtonText: ["Continue Session", "Fetch Fresh Data"],
        };
      case "TherapyNotFound":
        return {
          Title: "No Therapies Found",
          Message: `Run a Manual therapy on your ${deviceName} to fetch data.`,
          ButtonText: "Done",
        };
      case "pulseDeviceDisconnect":
        return {
          Title: "Disconnect Pulse Oximeter",
          Message: `Are you sure you want to disconnect from your Pulse Oximeter?`,
          ButtonText: "Disconnect",
        };
      case "connectedPulseOximeter":
        return {
          Title: "Pulse Oximeter Connected",
          Message: `You are now connected to your Pulse Oximeter.`,
          ButtonText: "Done",
        };
      default:
        return {
          Title: "Connect to Device",
          Message: `Would you like to connect to this ${deviceName}?`,
          ButtonText: "Connect",
        };
    }
  };

  // Simple PLX Device Connect Function with Full Service Discovery
  const connectToPLXDeviceSimple = async (
    device: ScanResult,
    deviceToDisconnectFrom?: string,
    onDataReceived?: (measurement: PLXMeasurement) => void
  ): Promise<{ status: number; message: string }> => {
    try {
      // Handle existing connection if needed
      if (
        deviceToDisconnectFrom &&
        deviceToDisconnectFrom !== device.device.deviceId
      ) {
        await BleClient.disconnect(deviceToDisconnectFrom);
      } else if (
        deviceToDisconnectFrom &&
        deviceToDisconnectFrom === device.device.deviceId
      ) {
        console.log("Already connected to device");
        return { status: 1, message: "Already connected" };
      }

      // Connect to the device
      console.log(
        "Connecting to device:",
        device.device.name,
        device.device.deviceId
      );
      await BleClient.connect(device.device.deviceId, async (deviceId) => {
        // console.log(`Pulse oximeter disconnected: ${deviceId}`);
        await onPulseOximeterDisconnect(deviceId);
      });

      if (isPlatform("android")) {
        const isBonded = await BleClient.isBonded(device.device.deviceId);
        if (!isBonded) {
          await BleClient.createBond(device.device.deviceId, {
            timeout: 60000,
          });
        }
      }

      console.log("✅ Connected! Now discovering services...");

      // Discover all services
      const services = await BleClient.getServices(device.device.deviceId);
      console.log(`📋 Found ${services.length} services:`);

      //  READ DEVICE INFORMATION (once at connection)
      // await readDeviceInformation(device.device.deviceId);

      //  READ BATTERY LEVEL (once at connection)
      // await readBatteryLevel(device.device.deviceId);

      // List all services and their characteristics
      services.forEach((service, serviceIndex) => {
        console.log(`\n🔍 Service ${serviceIndex + 1}:`);
        console.log(`   UUID: ${service.uuid}`);
        console.log(`   Characteristics: ${service.characteristics.length}`);

        service.characteristics.forEach((char, charIndex) => {
          const hasNotify = char.properties.notify;
          const hasIndicate = char.properties.indicate;
          const hasRead = char.properties.read;
          const hasWrite = char.properties.write;
          const hasWriteWithoutResponse = char.properties.writeWithoutResponse;
          const hasBroadcast = char.properties.broadcast;

          // Build properties display string
          const activeProperties = [];
          if (hasBroadcast) activeProperties.push("broadcast");
          if (hasRead) activeProperties.push("read");
          if (hasWriteWithoutResponse)
            activeProperties.push("writeWithoutResponse");
          if (hasWrite) activeProperties.push("write");
          if (hasNotify) activeProperties.push("notify");
          if (hasIndicate) activeProperties.push("indicate");
          if (char.properties.authenticatedSignedWrites)
            activeProperties.push("authenticatedSignedWrites");
          if (char.properties.reliableWrite)
            activeProperties.push("reliableWrite");
          if (char.properties.writableAuxiliaries)
            activeProperties.push("writableAuxiliaries");
          if (char.properties.extendedProperties)
            activeProperties.push("extendedProperties");

          const notificationTypes = [];
          if (hasNotify) notificationTypes.push("🔔 NOTIFY");
          if (hasIndicate) notificationTypes.push("🔕 INDICATE");

          console.log(`   📡 Characteristic ${charIndex + 1}:`);
          console.log(`      UUID: ${char.uuid}`);
          console.log(`      Properties: ${activeProperties.join(", ")}`);
          if (notificationTypes.length > 0) {
            console.log(
              `      🎯 Available notifications: ${notificationTypes.join(
                ", "
              )}`
            );
          }
        });
      });

      // Set up listeners for all notify/indicate characteristics
      let listenersCount = 0;
      let notifyCount = 0;
      let indicateCount = 0;

      for (const service of services) {
        for (const characteristic of service.characteristics) {
          const hasNotify = characteristic.properties.notify;
          const hasIndicate = characteristic.properties.indicate;
          const canNotify = hasNotify || hasIndicate;

          if (canNotify) {
            try {
              const notificationType =
                hasNotify && hasIndicate
                  ? "notify + indicate"
                  : hasNotify
                  ? "notify"
                  : "indicate";

              // Build properties display string
              const activeProperties = [];
              if (characteristic.properties.broadcast)
                activeProperties.push("broadcast");
              if (characteristic.properties.read) activeProperties.push("read");
              if (characteristic.properties.writeWithoutResponse)
                activeProperties.push("writeWithoutResponse");
              if (characteristic.properties.write)
                activeProperties.push("write");
              if (characteristic.properties.notify)
                activeProperties.push("notify");
              if (characteristic.properties.indicate)
                activeProperties.push("indicate");
              if (characteristic.properties.authenticatedSignedWrites)
                activeProperties.push("authenticatedSignedWrites");
              if (characteristic.properties.reliableWrite)
                activeProperties.push("reliableWrite");
              if (characteristic.properties.writableAuxiliaries)
                activeProperties.push("writableAuxiliaries");
              if (characteristic.properties.extendedProperties)
                activeProperties.push("extendedProperties");

              console.log(`\n🎧 Setting up ${notificationType} listener for:`);
              console.log(`   Service: ${service.uuid}`);
              console.log(`   Characteristic: ${characteristic.uuid}`);
              console.log(`   Properties: ${activeProperties.join(", ")}`);

              await BleClient.startNotifications(
                device.device.deviceId,
                service.uuid,
                characteristic.uuid,
                (value: DataView) => {
                  // Log the raw data info
                  console.log(
                    `\n📨 ${notificationType.toUpperCase()} DATA RECEIVED:`
                  );
                  console.log(`   From Service: ${service.uuid}`);
                  console.log(`   From Characteristic: ${characteristic.uuid}`);
                  console.log(`   Notification Type: ${notificationType}`);
                  console.log(`   Data Length: ${value.byteLength} bytes`);

                  // Try to parse as text
                  try {
                    const textData = dataViewToText(value);
                    console.log(`   📝 As Text: "${textData}"`);
                  } catch (error) {
                    console.log(
                      `   📝 As Text: [Cannot convert to text - ${
                        (error as Error).message
                      }]`
                    );
                  }

                  // Try to parse as numbers
                  try {
                    const numberData = dataViewToNumbers(value);
                    console.log(`   🔢 As Numbers: [${numberData.join(", ")}]`);
                  } catch (error) {
                    console.log(
                      `   🔢 As Numbers: [Cannot convert to numbers - ${
                        (error as Error).message
                      }]`
                    );
                  }

                  // Show raw hex data for debugging
                  const hexArray = [];
                  for (let i = 0; i < value.byteLength; i++) {
                    hexArray.push(
                      "0x" +
                        value
                          .getUint8(i)
                          .toString(16)
                          .padStart(2, "0")
                          .toUpperCase()
                    );
                  }

                  // Show raw bytes as decimal
                  const decimalArray = [];
                  for (let i = 0; i < value.byteLength; i++) {
                    decimalArray.push(value.getUint8(i));
                  }
                  // Parse PLX data and get the actual measurement
                  const parsedMeasurement = parsePLXData(
                    value,
                    service.uuid,
                    characteristic.uuid
                  );

                  // If callback provided and this is PLX data, parse and call it
                  if (parsedMeasurement && onDataReceived) {
                    // console.log(
                    //   "📊 Calling data callback with parsed measurement:",
                    //   parsedMeasurement
                    // );
                    onDataReceived(parsedMeasurement);
                  }
                  console.log(`   ----------------------------------------`);
                }
              );

              listenersCount++;
              if (hasNotify) notifyCount++;
              if (hasIndicate) indicateCount++;

              console.log(
                `✅ ${notificationType} listener ${listenersCount} set up successfully`
              );
            } catch (error) {
              console.error(
                `❌ Failed to set up listener for ${service.uuid}/${characteristic.uuid}:`,
                error
              );
              console.error(`   Error details:`, error);
            }
          }
        }
      }

      console.log(`\n🎉 Setup complete!`);
      console.log(`   Total services discovered: ${services.length}`);
      console.log(`   Total listeners set up: ${listenersCount}`);
      console.log(`   - Notify listeners: ${notifyCount}`);
      console.log(`   - Indicate listeners: ${indicateCount}`);
      console.log(`   Now listening for all notifications and indications...`);

      return {
        status: 1,
        message: `Connected successfully. ${services.length} services found, ${listenersCount} listeners active (${notifyCount} notify, ${indicateCount} indicate).`,
      };
    } catch (error) {
      console.error("❌ Failed to connect to device:", error);
      const bleError = error as BleError;

      if (
        bleError?.errorMessage?.includes("Peer removed pairing information") ||
        bleError?.errorMessage?.includes(
          "Writing characteristic failed with status code 201."
        )
      ) {
        return {
          status: 2,
          message:
            "Peer removed pairing information! Please forget the Device from Settings and retry connecting to the Device.",
        };
      }

      return {
        status: 0,
        message:
          bleError?.errorMessage ||
          (error as Error)?.message ||
          "Failed to connect to device",
      };
    }
  };

  const handleFindDevice = useCallback(() => {
    router.push("/remote/scan");
  }, []);

  const logDeviceDiscovery = (
    deviceType: DeviceType,
    deviceName: string,
    deviceId: string
  ) => {
    const typeStr =
      deviceType === DeviceType.THERAPY
        ? "Therapy"
        : deviceType === DeviceType.PULSE_OXIMETER
        ? "Pulse Oximeter"
        : "Unknown";
    console.log(`📱 Discovered ${typeStr} device: ${deviceName} (${deviceId})`);
  };

  // Increased scan duration with shorter pause for better user experience
  const SCAN_DURATION = 15000; // 30 seconds of scanning
  const PAUSE_DURATION = 2000; // 2 seconds pause

  // serverStatus reply
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const serverStatusQueueRef = useRef<string[]>([]);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Use useRef instead of useState to avoid re-renders and ensure values persist
  const therapyParamsPart1Ref = useRef<TherapyParamsPart1 | null>(null);
  const therapyParamsPart2Ref = useRef<TherapyParamsPart2 | null>(null);
  let receivedAdherenceChunks: Uint8Array[] = [];
  let receivedTitrationChunks: Uint8Array[] = [];
  let receivedCurveChunks: Uint8Array[] = [];
  let receivedMainMapChunks: Uint8Array[] = [];
  let receivedValMapChunks: Uint8Array[] = [];
  let receivedLogsChunks: Uint8Array[] = [];
  let receivedUpdatedTitrationChunks: Uint8Array[] = [];
  let receivedUpdatedCurvesListChunks: Uint8Array[] = [];
  const logStringList: {
    reconstructedLogString: string;
    event_id: string;
    batch_id: string;
  }[] = [];
  let mainMap1 = "";
  let valMap1 = "";
  const buttonText = getModalMessages().ButtonText;

  //Function for glucose Measurement message handling
  const handleGlucoseMessage = async (data1: number[]) => {
    const uint8Data = new Uint8Array(data1);
    const result = parseAndLogChunk(uint8Data);

    if (result.errorMessage) {
      console.error("Parsing error:", result.errorMessage);
      return;
    }

    const {
      messageType,
      chunkPosition,
      chunkNumber,
      totalChunks,
      includedMeasurements,
      refId,
      eventId,
    } = result.header;

    // Helper function to calculate progress
    const calculateProgress = (chunkNum: number, totalChunks: number) => {
      if (chunkPosition === ChunkPosition.SINGLE) {
        return 100;
      }
      return Math.round((chunkNum / totalChunks) * 100);
    };

    // Helper function to reset progress after completion
    const handleProgressCompletion = (setProgress: (value: number) => void) => {
      setTimeout(() => setProgress(0), 500); // Small delay to show 100% briefly
    };

    switch (messageType) {
      case MessageType.ADHERENCE: {
        receivedAdherenceChunks.push(result.payload);
        // console.log("Adherence data received:", result.payload);
        const isPartial =
          totalChunks > 20 && chunkNumber > 0 && (chunkNumber + 1) % 20 === 0;

        // Update progress
        const adherenceProgressValue = calculateProgress(
          chunkNumber,
          totalChunks
        );
        setAdherenceProgress(adherenceProgressValue);
        resetInsightsProgressTimer("adherence", () => {
          // console.log("Adherence data fetch reset");
          setAdherenceData(null); // Reset data on timeout
          setAdherenceProgress(0); // Reset progress
          setAdherenceLoading(false); // Reset loading state
          setAdherenceBackgroundLoading(false); // Reset background loading state
          receivedAdherenceChunks = []; // Clear received chunks
        });

        if (
          chunkPosition === ChunkPosition.SINGLE ||
          chunkPosition === ChunkPosition.END ||
          isPartial
        ) {
          setAdherenceBackgroundLoading(true);
          await reconstructMessage(
            messageType,
            0,
            0,
            result.payload,
            includedMeasurements,
            result.header
          );
          handleProgressCompletion(setAdherenceProgress);
        }
        if (isPartial && deviceIdRef.current && isConnectedRef.current) {
          await BleClient.write(
            deviceIdRef.current,
            GLUCOSE_SERVICE,
            GLUCOSE_MEASUREMENT_CHARACTERISTIC,
            textToDataView(`sendNextAdherence`)
          );
        }
        break;
      }
      case MessageType.CURVES:
        {
          receivedCurveChunks.push(result.payload);
          // console.log("Curves data received:", result.payload);

          // Update progress
          const curvesProgressValue = calculateProgress(
            chunkNumber,
            totalChunks
          );
          setCurvesProgress(curvesProgressValue);
          resetInsightsProgressTimer("curves", () => {
            // console.log("Curves data fetch reset");
            setCurveData(null); // Reset data on timeout
            setCurvesProgress(0); // Reset progress
            setCurveLoading(false); // Reset loading state
            receivedCurveChunks = []; // Clear received chunks
          });

          if (
            chunkPosition === ChunkPosition.SINGLE ||
            chunkPosition === ChunkPosition.END
          ) {
            await reconstructMessage(
              messageType,
              0,
              0,
              result.payload,
              includedMeasurements
            );
            handleProgressCompletion(setCurvesProgress);
          } else {
            if (
              deviceIdRef.current &&
              isConnectedRef.current &&
              (chunkNumber + 1) % 50 === 0
            ) {
              await BleClient.write(
                deviceIdRef.current,
                GLUCOSE_SERVICE,
                GLUCOSE_MEASUREMENT_CHARACTERISTIC,
                textToDataView(`sendNextCurves`)
              );
            }
          }
        }
        break;
      case MessageType.TITRATION: {
        receivedTitrationChunks.push(result.payload);
        // console.log("Titration data received111:", result.payload);

        // Update progress
        const titrationProgressValue = calculateProgress(
          chunkNumber,
          totalChunks
        );
        setTitrationProgress(titrationProgressValue);
        resetInsightsProgressTimer("titration", () => {
          // console.log("Titration data fetch reset");
          setTitrationData(null); // Reset data on timeout
          setTitrationProgress(0); // Reset progress
          setTitrationLoading(false); // Reset loading state
          receivedTitrationChunks = []; // Clear received chunks
        });

        if (
          chunkPosition === ChunkPosition.SINGLE ||
          chunkPosition === ChunkPosition.END
        ) {
          await reconstructMessage(messageType);
          handleProgressCompletion(setTitrationProgress);
        }
        break;
      }
      case MessageType.UPDATED_TITRATION_SUMMARY:
        receivedUpdatedTitrationChunks.push(result.payload);
        // console.log("NEW Titration data received111:", result.payload);
        if (
          chunkPosition === ChunkPosition.SINGLE ||
          chunkPosition === ChunkPosition.END
        ) {
          await reconstructMessage(messageType);
        }
        break;

      case MessageType.UPDATED_CURVES_LIST:
        receivedUpdatedCurvesListChunks.push(result.payload);
        // console.log("New Curves List data received111:", result.payload);
        if (
          chunkPosition === ChunkPosition.SINGLE ||
          chunkPosition === ChunkPosition.END
        ) {
          await reconstructMessage(messageType);
        }
        break;

      case MessageType.MAIN_MAP:
        receivedMainMapChunks.push(result.payload);
        // console.log("MAIN MAP data received:", result.payload);
        if (
          chunkPosition === ChunkPosition.SINGLE ||
          chunkPosition === ChunkPosition.END
        ) {
          await reconstructMessage(messageType);
        }
        break;

      case MessageType.VAL_MAP:
        receivedValMapChunks.push(result.payload);
        if (
          chunkPosition === ChunkPosition.SINGLE ||
          chunkPosition === ChunkPosition.END
        ) {
          await reconstructMessage(messageType);
        }
        break;

      case MessageType.LOGS:
        receivedLogsChunks.push(result.payload);
        if (
          chunkPosition === ChunkPosition.SINGLE ||
          chunkPosition === ChunkPosition.END
        ) {
          await reconstructMessage(messageType, refId, eventId);
        }
        break;
      case MessageType.REALTIME_CURVES:
        if (
          chunkPosition === ChunkPosition.SINGLE ||
          chunkPosition === ChunkPosition.END
        ) {
          await reconstructMessage(
            messageType,
            0,
            0,
            result.payload,
            includedMeasurements
          );
        }
        break;

      default:
        // uncaughtMessages.push(data1);
        console.error("Uncaught message type:", messageType);
    }
  };
  // Function to decode phase from numeric code
  const decodePhase = (phaseCode: number): TherapyPhase => {
    switch (phaseCode) {
      case 0:
        return TherapyPhase.INACTIVE;
      case 1:
        return TherapyPhase.PAUSE;
      case 2:
        return TherapyPhase.INSPIRATORY;
      case 3:
        return TherapyPhase.EXPIRATORY;
      default:
        return TherapyPhase.INACTIVE;
    }
  };
  // Update complete therapy parameters when both parts are available
  const updateCompleteTherapyParams = () => {
    // Only proceed if both part references have data
    if (therapyParamsPart1Ref.current && therapyParamsPart2Ref.current) {
      // console.log(
      //   "Both therapy param parts available - updating complete parameters"
      // );

      const part1 = therapyParamsPart1Ref.current;
      const part2 = therapyParamsPart2Ref.current;

      const cycleDuration =
        part1.pauseDuration + part1.inspDuration + part1.expDuration;

      setTherapyParams({
        pausePressure: part2.pausePressure,
        inspPressure: part2.inspPressure,
        expPressure: part2.expPressure,
        pauseDuration: part1.pauseDuration,
        inspDuration: part1.inspDuration,
        expDuration: part1.expDuration,
        cycleDuration: cycleDuration,
        enabledPhases: part1.enabledPhases,
        inspTriggerStatus: part1.inspTriggerStatus,
        currentCycleNumber: part1.currentCycleNumber,
        totalCycleCount: part2.totalCycles,
        oscStatus: part2.oscStatus,
        // Other parameters you might need
      });

      // console.log("Updated therapy parameters:", {
      //   pausePressure: part2.pausePressure,
      //   inspPressure: part2.inspPressure,
      //   expPressure: part2.expPressure,
      //   pauseDuration: part1.pauseDuration,
      //   inspDuration: part1.inspDuration,
      //   expDuration: part1.expDuration,
      //   cycleDuration: cycleDuration,
      //   enabledPhases: part1.enabledPhases,
      // });
    } else {
      console.log("Cannot update parameters, missing parts:", {
        part1: therapyParamsPart1Ref.current,
        part2: therapyParamsPart2Ref.current,
      });
    }
  };

  const writeSendBuffMessageOverBLE = async (deviceId?: string) => {
    if (!deviceId) {
      if (isConnectedRef.current && deviceIdRef.current) {
        // console.log("device", deviceIdRef.current);
        await BleClient.write(
          deviceIdRef.current,
          GLUCOSE_SERVICE,
          GLUCOSE_MEASUREMENT_CHARACTERISTIC,
          textToDataView("sendLogs")
        );
      }
    }
  };

  // Function to process the queue of server status messages
  const processServerStatusQueue = async (deviceId: string) => {
    // If not connected or queue is empty, stop processing
    if (!isConnectedRef.current || serverStatusQueueRef.current.length === 0) {
      setIsProcessingQueue(false);
      return;
    }

    try {
      // Get the next message from the queue
      const nextMessage = serverStatusQueueRef.current.shift();

      if (nextMessage && deviceId) {
        // console.log(`Sending server status: ${nextMessage}`);

        // Send the message to the device
        await BleClient.write(
          deviceId,
          GLUCOSE_SERVICE,
          GLUCOSE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(nextMessage)
        );
      }

      // Set timeout for next message (200ms interval)
      processingTimeoutRef.current = setTimeout(async () => {
        await processServerStatusQueue(deviceId);
      }, 200);
    } catch (error) {
      console.error("Error sending server status message:", error);
      // If there's an error (like disconnection), clear the queue
      // Define a proper error interface for BLE errors
      if (
        (error as BleError)?.errorMessage?.includes("Not connected to device.")
      ) {
        clearServerStatusQueue();
      } else {
        // For other errors, continue processing the queue
        processingTimeoutRef.current = setTimeout(async () => {
          await processServerStatusQueue(deviceId);
        }, 200);
      }
    }
  };

  /**
   * Sends a request for real-time curves data with selective parameters
   * @param pressure - Whether to include pressure data
   * @param flow - Whether to include flow data
   * @param volume - Whether to include volume data
   * @param intervalMs - Transmission interval in milliseconds (optional, defaults to 100ms)
   *
   * The param1 uses a 3-bit mask:
   * - Bit 0: Pressure data
   * - Bit 1: Flow data
   * - Bit 2: Volume data
   *
   * Examples:
   * - pressure=true, flow=true, volume=false → param1 = 3 (binary: 011)
   * - pressure=true, flow=true, volume=true → param1 = 7 (binary: 111)
   * - pressure=false, flow=false, volume=false → param1 = 0 (binary: 000)
   */
  const askForRealTimeCurves = async (
    pressure: boolean,
    flow: boolean,
    volume: boolean,
    intervalMs = 100
  ) => {
    if (isConnectedRef.current && deviceIdRef.current) {
      try {
        // Calculate param1 using bitwise operations
        let param1 = 0;
        if (pressure) param1 |= 1 << 0; // Set bit 0 for pressure
        if (flow) param1 |= 1 << 1; // Set bit 1 for flow
        if (volume) param1 |= 1 << 2; // Set bit 2 for volume

        // Create the command string
        const command = `sendRealTimeCurves,${param1},${intervalMs}`;

        // Debug logging
        const selectedData = [];
        if (pressure) selectedData.push("pressure");
        if (flow) selectedData.push("flow");
        if (volume) selectedData.push("volume");

        // Send the command via BLE
        await BleClient.write(
          deviceIdRef.current,
          GLUCOSE_SERVICE,
          GLUCOSE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(command)
        );

        // console.log("✅ Real-time curves request sent successfully");
      } catch (error) {
        console.error("❌ Failed to ask for real-time curves data:", error);
      }
    } else {
      console.warn(
        "⚠️ Not connected or device ID not set for real-time curves"
      );
    }
  };

  const askForPressureGaugeData = async () => {
    if (isConnectedRef.current && deviceIdRef.current) {
      try {
        // console.log("Asking for pressure gauge data");
        await BleClient.write(
          deviceIdRef.current,
          GLUCOSE_SERVICE,
          GLUCOSE_MEASUREMENT_CHARACTERISTIC,
          textToDataView("sendPressureGauge")
        );
      } catch (error) {
        console.error("Failed to ask for pressure gauge data:", error);
      }
    } else {
      console.warn("Not connected or device ID not set for pressure gauge");
    }
  };

  const askForMaps = async (device?: ScanResult) => {
    // console.log("Asking for main and value maps");
    if (device && device.device.deviceId) {
      await BleClient.write(
        device.device.deviceId,
        GLUCOSE_SERVICE,
        GLUCOSE_MEASUREMENT_CHARACTERISTIC,
        textToDataView("sendMainMap")
      );
      await BleClient.write(
        device.device.deviceId,
        GLUCOSE_SERVICE,
        GLUCOSE_MEASUREMENT_CHARACTERISTIC,
        textToDataView("sendValMap")
      );
    }
  };

  // Function to add messages to the queue and start processing if not already
  const addToServerStatusQueue = (messages: string[], deviceId: string) => {
    // Add new messages to the queue
    serverStatusQueueRef.current.push(...messages);

    // If not already processing, start processing
    if (!isProcessingQueue) {
      setIsProcessingQueue(true);
      processServerStatusQueue(deviceId);
    }
  };

  // Function to clear the queue and any pending timeouts
  const clearServerStatusQueue = () => {
    // Clear the queue
    serverStatusQueueRef.current = [];

    // Clear any pending timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }

    setIsProcessingQueue(false);
  };

  // Helper function to clear timeouts
  const clearScanTimeouts = () => {
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
  };

  const setConnectionStatus = (status: boolean, device?: ScanResult | null) => {
    setIsConnected(status);
    setDevice(device || null);
  };

  // Add new pulse oximeter connection setter
  const setPulseOximeterConnectionStatus = (
    status: boolean,
    device?: ScanResult | null
  ) => {
    setIsPulseOximeterConnected(status);
    setPulseOximeterDevice(device || null);
  };

  async function retryWrite(
    deviceId: string,
    service: string,
    characteristic: string,
    data: DataView,
    retries = 12
  ) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const msg = await BleClient.write(
          deviceId,
          service,
          characteristic,
          data
        );
        return msg; // Exit the function if write is successful
      } catch (error) {
        console.error(`Write Attempt ${attempt} Failed`, error);
        if (attempt === retries) {
          //add console log to show the error
          console.error("Max retries reached. Write failed:", error);
          // alert("Enable Pairing on the device and Retry");
          // window.location.assign('/');
          throw error; // Re-throw the error after exhausting retries
        }
      }
    }
  }

  /**
   * Extracts device type and serial number from a device string
   * Expected format: "BRAND DEVICE_TYPE SERIAL_NUMBER" (e.g., "ABM Cough CNS22A0031234567891")
   *
   * @param deviceString - The device string to parse
   * @returns Object containing deviceType and serialNumber
   */
  function parseDeviceString(deviceString: string): {
    deviceType: string;
    serialNumber: string;
  } {
    // Handle null, undefined, or empty string
    if (!deviceString || typeof deviceString !== "string") {
      return {
        deviceType: "Cough",
        serialNumber: "unknown",
      };
    }

    // Handle null, undefined, or empty string
    if (deviceString && deviceString.includes("Device")) {
      const parts = deviceString
        .trim()
        .split("-")
        .filter((part) => part.length > 0);

      // console.log("parts", parts);

      return {
        deviceType: "BiWaze® Cough",
        serialNumber: parts[1] || "unknown", // Second part is serial number
      };
    }

    // Split by spaces and filter out empty strings
    const parts = deviceString
      .trim()
      .split(/\s+/)
      .filter((part) => part.length > 0);

    // Check if we have exactly 3 parts
    if (parts.length >= 3) {
      return {
        deviceType: parts[1] === "COUGH" ? "BiWaze® Cough" : "BiWaze® Clear", // Second part is device type
        serialNumber: parts[2], // Third part is serial number
      };
    }

    // Return defaults if not 3 parts
    return {
      deviceType: "Cough",
      serialNumber: "unknown",
    };
  }

  const initBle = async () => {
    const openSettings = async () => {
      await BleClient.openAppSettings();
    };
    try {
      // console.log("Initializing BLE...");
      await BleClient.initialize();
      setIsBleInitialized(true);
      if (storage && location.pathname.includes("/remote")) {
        // reconnectToSavedDevice(); // Try reconnecting after storage is ready
        startScanningCycle();
      }
    } catch (e) {
      alert("Please enable Bluetooth to use the Remote Control feature");
      openSettings();
      history.push("/home");
    }
    // console.log("INIT....");
  };

  const handleBLECheck = async () => {
    if (isBleInitialized) {
      // console.log("BLE is initialized");
    } else {
      // console.log("BLE is not initialized");
      if (location.pathname.includes("/remote")) {
        await initBle();
      }
    }
  };

  const startScanning = async () => {
    try {
      // Load previous devices for auto-connection
      const prevConnectedDevice = await loadDeviceFromStorage(storage);
      const prevPulseOximeterDevice = await loadPulseOximeterFromStorage(
        storage
      );

      setScanningStatus("scanning");
      await BleClient.stopLEScan();

      // Enhanced scan - remove namePrefix filter to catch both device types
      await BleClient.requestLEScan(
        {
          scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
          services: [
            HEART_RATE_SERVICE,
            GLUCOSE_SERVICE,
            // PULSE_OXIMETER_SERVICE,
          ], // Reducing number of devices scanned by filtering to known services (app crash identified while continuously scanning all devices when connected to a BiWaze device ON IOS device app object being changed and same object is referenced while writing messages to device)
          allowDuplicates: false, // further reducing number of devices scanned
        },
        async (result: ScanResult) => {
          const deviceType = getDeviceType(result.device.name || "");

          if (deviceType === DeviceType.THERAPY) {
            logDeviceDiscovery(
              deviceType,
              result.device.name || "Unknown",
              result.device.deviceId
            );
            // Handle therapy device (existing logic)
            setDevices((prevDevices) => {
              const exists = prevDevices.some(
                (d) => d.device.deviceId === result.device.deviceId
              );
              if (!exists) {
                if (prevDevices.length === 0) {
                  setNewDeviceIndex(0);
                }
                return [...prevDevices, result];
              } else {
                return prevDevices;
              }
            });

            // Auto-connect to therapy device (existing logic)
            if (
              !isConnected &&
              !isConnectedRef.current &&
              prevConnectedDevice &&
              prevConnectedDevice.deviceId === result.device.deviceId &&
              (!lastDeviceManuallyDisconnectedFromRef.current ||
                lastDeviceManuallyDisconnectedFromRef.current !==
                  result.device.deviceId)
            ) {
              console.log(
                "Auto-connecting to previously connected therapy device:",
                "from scan:",
                result.device.name,
                "from storage:",
                prevConnectedDevice.deviceId
              );
              await connectToDevice(result);
            }
          } else if (deviceType === DeviceType.PULSE_OXIMETER) {
            logDeviceDiscovery(
              deviceType,
              result.device.name || "Unknown",
              result.device.deviceId
            );
            // Handle pulse oximeter device discovery
            setPulseOximeterDevices((prevDevices) => {
              const exists = prevDevices.some(
                (d) => d.device.deviceId === result.device.deviceId
              );
              if (!exists) {
                console.log("Discovered pulse oximeter:", result.device.name);
                return [...prevDevices, result];
              }
              return prevDevices;
            });
            if (
              !isPulseOximeterConnected &&
              prevPulseOximeterDevice &&
              prevPulseOximeterDevice.deviceId === result.device.deviceId &&
              (!lastPulseOximeterManuallyDisconnectedFromRef.current ||
                lastPulseOximeterManuallyDisconnectedFromRef.current !==
                  result.device.deviceId)
            ) {
              console.log(
                "Auto-connecting to previously connected pulse oximeter device:",
                "from scan:",
                result.device.name,
                "from storage:",
                prevPulseOximeterDevice.deviceId
              );
              await connectToPulseOximeterDevice(result);
            }
          }
          // All other device types (not ABM or Non) are filtered out
        }
      );
    } catch (error) {
      console.error("Failed to start BLE scan:", error);
      setScanningStatus("paused");
    }
  };

  const pauseScanning = async () => {
    try {
      // console.log('Pausing scan:', new Date().toISOString());
      await BleClient.stopLEScan();
      setScanningStatus("paused");
    } catch (error) {
      console.error("Failed to pause BLE scan:", error);
    }
  };

  const stopScanning = async () => {
    try {
      // console.log("Stopping scan completely:", new Date().toISOString());
      await BleClient.stopLEScan();
      setScanningStatus("stopped");
      // Clear both device lists when stopping
      setDevices([]);
      setPulseOximeterDevices([]);
    } catch (error) {
      console.error("Failed to stop BLE scan:", error);
    }
  };

  const startScanningCycle = () => {
    // Start first scan immediately
    startScanning();
  };

  const stopScanningCycle = () => {
    stopScanning();
  };

  const loadInsightsDataFromStorage = async (forDeviceSerial: string) => {
    if (storageRef.current) {
      const StoredAdherenceData = await getLastFetchedAdherence(
        storageRef.current
      );
      // console.log("Adherence data from storage:", StoredAdherenceData);
      setAdherenceData(StoredAdherenceData?.adherenceData || null);
      setStoredAdherenceDeviceId(
        StoredAdherenceData?.deviceId || "Unknown Device"
      );

      const savedTitrationSessions = await getAllTitrationSessions(
        storageRef.current
      );

      const savedSessionsDevice = savedTitrationSessions
        .filter(
          (session) =>
            session.session.locallyAvailable === true &&
            session.deviceId === forDeviceSerial
        )
        .sort((a, b) =>
          b.session.refId.localeCompare(a.session.refId, undefined, {
            numeric: true,
          })
        );
      // console.log(
      //   "Locally available titration sessions init1:",
      //   savedSessionsDevice
      // );
      const locallyAvailableTitrationTherapiesList = savedSessionsDevice.map(
        (session) => session.session
      );
      setTitrationTherapiesList(locallyAvailableTitrationTherapiesList);
      // console.log("Titration data from storage:", StoredTitrationData);
      setTitrationData(savedSessionsDevice[0]?.titrationData || null);
      setStoredTitrationDeviceId(
        savedSessionsDevice[0]?.deviceId || "Unknown Device"
      );

      const StoredCurveSessions = await getAllCurveAnalysisSessions(
        storageRef.current
      );
      // console.log("Curve sessions from storage:", StoredCurveSessions);

      const savedCurveSessionsDevice = StoredCurveSessions.filter(
        (session) =>
          session.therapySession.locallyAvailable === true &&
          session.deviceId === forDeviceSerial
      ).sort((a, b) =>
        b.therapySession.refId.localeCompare(
          a.therapySession.refId,
          undefined,
          {
            numeric: true,
          }
        )
      );
      const savedCurvesList = savedCurveSessionsDevice.map(
        (session) => session.therapySession
      );
      // console.log("Saved curves list init1:", savedCurvesList);
      setCurvesList(savedCurvesList);
      const storedCurveData = await getCurveAnalysisSessionById(
        storageRef.current,
        savedCurvesList && savedCurvesList[0]?.refId
      );
      // console.log("Curve data from storage:", storedCurveData);
      setCurveData(storedCurveData?.curveAnalysisData || null);
      setStoredCurveDataDeviceId(storedCurveData?.deviceId || "Unknown Device");
    }
  };

  const onDisconnect = async (deviceId: string) => {
    receivedAdherenceChunks.length = 0;
    receivedTitrationChunks.length = 0;
    receivedCurveChunks.length = 0;
    receivedMainMapChunks.length = 0;
    receivedValMapChunks.length = 0;
    receivedLogsChunks.length = 0;
    receivedUpdatedTitrationChunks.length = 0;
    receivedUpdatedCurvesListChunks.length = 0;
    logStringList.length = 0;
    mainMap1 = "";
    valMap1 = "";
    startScanningCycle();
    setDevices((prevDevices) =>
      prevDevices.filter((d) => d.device.deviceId !== deviceId)
    );
    // console.log("hiddenDisconnect", hiddenDisconnect);
    setConnectionStatus(false, null);
    setTherapyState(false);
    therapyParamsPart1Ref.current = null;
    therapyParamsPart2Ref.current = null;
    //  setDevices([]);
    stopAllTimers();
    setMainMap("");
    setValMap("");
    setRealtimeData({
      flowData: [],
      pressureData: [],
      volumeData: [],
    });
    setRealtimeDataPoints({
      flowData: [],
      pressureData: [],
      volumeData: [],
    });
    if (deviceNameRef.current) {
      await loadInsightsDataFromStorage(
        parseDeviceString(deviceNameRef.current).serialNumber
      );
    }
    deviceIdRef.current = null;
    isConnectedRef.current = false;
    deviceNameRef.current = null;
  };
  const onPulseOximeterDisconnect = async (deviceId: string) => {
    startScanningCycle();
    setPulseOximeterDevices((prevDevices) =>
      prevDevices.filter((d) => d.device.deviceId !== deviceId)
    );
    // console.log("hiddenDisconnect", hiddenDisconnect);
    setPulseOximeterConnectionStatus(false, null);
    setPulseOximeterData(null);
  };
  const toggleTherapyState = async () => {
    if (device && !ribbonState && isConnected) {
      try {
        await BleClient.write(
          device.device.deviceId,
          HEART_RATE_SERVICE,
          HEART_RATE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(`play,${therapyState}`)
        );
      } catch (error) {
        console.error("Error toggling therapy state:", error);
        openModal(
          "closeDeviceOverlay",
          device,
          "Failed to toggle therapy state. Please try again."
        );
      }
    } else if (device && ribbonState) {
      openModal("closeDeviceOverlay", device);
    } else {
      openModal("notConnectedToDevice", null);
    }
  };

  const handleInspPhaseStart = async () => {
    if (!isConnected || !therapyState) return;

    // setPlusActive(true);
    try {
      if (device && ribbonState) {
        openModal("closeDeviceOverlay", device);
        return;
      }
      if (device)
        await BleClient.write(
          device.device.deviceId,
          HEART_RATE_SERVICE,
          HEART_RATE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(`positive,${1}`)
        );
      // setSeconds(0);
    } catch (error) {
      console.error("Error with positive button:", error);
    }
  };

  const handleInspPhaseStop = async () => {
    if (!isConnected || !therapyState) return;

    // setPlusActivxe(false);
    try {
      if (device && ribbonState) {
        openModal("closeDeviceOverlay", device);
        return;
      }
      if (device)
        await BleClient.write(
          device.device.deviceId,
          HEART_RATE_SERVICE,
          HEART_RATE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(`positiveReleased,${1}`)
        );
      // setSeconds(0);
    } catch (error) {
      console.error("Error with positive button release:", error);
    }
  };

  const handleExpPhaseStart = async () => {
    if (!isConnected || !therapyState) return;

    // setMinusActive(true);
    try {
      if (device && ribbonState) {
        openModal("closeDeviceOverlay", device);
        return;
      }
      if (device)
        await BleClient.write(
          device.device.deviceId,
          HEART_RATE_SERVICE,
          HEART_RATE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(`negative,${-1}`)
        );
      // setSeconds(0);
    } catch (error) {
      console.error("Error with negative button:", error);
    }
  };

  const handleExpPhaseStop = async () => {
    if (!isConnected || !therapyState) return;

    // setMinusActive(false);
    try {
      if (device && ribbonState) {
        openModal("closeDeviceOverlay", device);
        return;
      }
      if (device)
        await BleClient.write(
          device.device.deviceId,
          HEART_RATE_SERVICE,
          HEART_RATE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(`negativeReleased,${-1}`)
        );
      // setSeconds(0);
    } catch (error) {
      console.error("Error with negative button release:", error);
    }
  };

  async function reconstructMessage(
    msgType: MessageType,
    refId?: number,
    event_id?: number,
    payload?: Uint8Array,
    includedMeasurements?: number,
    header?: ChunkHeader
  ) {
    // Concatenate all chunks into the full message
    if (msgType === MessageType.MAIN_MAP) {
      // You can reset the chunks array here if the message is complete// Example: If the message ends with a specific delimiterif (fullMessage.includes('|CRC:')) {
      const merged = mergeChunks(receivedMainMapChunks); // merge Uint8Arrays
      mainMap1 = new TextDecoder().decode(merged);
      setMainMap(mainMap1);
      receivedMainMapChunks = [];
      // console.log(
      //   "main MAP received:",
      //   JSON.parse(mainMap1).OrderedData,
      //   "mesg received at:",
      //   new Date(),
      //   isConnectedRef.current,
      //   deviceIdRef.current
      // );
    } else if (msgType === MessageType.VAL_MAP) {
      const merged = mergeChunks(receivedValMapChunks); // merge Uint8Arrays
      valMap1 = new TextDecoder().decode(merged);
      setValMap(valMap1);
      // console.log(
      //   "val Map  received:",
      //   JSON.parse(valMap1),
      //   "mesg received at:",
      //   new Date()
      // );
      receivedValMapChunks = [];
    } else if (msgType === MessageType.ADHERENCE) {
      const merged = mergeChunks(receivedAdherenceChunks); // merge Uint8Arrays
      const text = new TextDecoder().decode(merged);
      const isPartial =
        header &&
        header.totalChunks > 20 &&
        header.chunkNumber > 0 &&
        ((header.chunkNumber + 1) % 20 === 0 ||
          header.chunkPosition === ChunkPosition.END);
      const adherenceData = parseAdherenceFromText(text, isPartial);
      console.log("Adherence data received:", adherenceData);
      setAdherenceData(adherenceData);
      setAdherenceLoading(false);
      stopInsightsProgressTimer("adherence");
      if (storageRef.current && deviceNameRef.current && adherenceData) {
        await storeAdherenceData(
          storageRef.current,
          adherenceData,
          parseDeviceString(deviceNameRef.current).serialNumber
        );
      }
      if (header && header.chunkPosition === ChunkPosition.END) {
        setAdherenceBackgroundLoading(false);
        receivedAdherenceChunks = []; // Clear the chunks array for the next message
      }
    } else if (msgType === MessageType.TITRATION) {
      const merged = mergeChunks(receivedTitrationChunks); // merge Uint8Arrays
      const titrationData = parseBLETitrationData(merged);
      console.log("Titration data received:", titrationData);
      setTitrationData(titrationData);
      setTitrationLoading(false);
      stopInsightsProgressTimer("titration");

      // Store the session first, then update the list based on what's actually stored
      if (
        storageRef.current &&
        deviceNameRef.current &&
        currentTitrationSessionRef.current
      ) {
        try {
          console.log(
            "Storing titration session:",
            currentTitrationSessionRef.current
          );

          // Store the session first
          await storeTitrationSession(
            storageRef.current,
            {
              ...currentTitrationSessionRef.current,
              locallyAvailable: true,
            },
            titrationData,
            parseDeviceString(deviceNameRef.current).serialNumber
          );

          // After storing, fetch all available sessions to get accurate state
          const storedSessions = await getAllTitrationSessions(
            storageRef.current
          );

          // console.log("Stored Sessions after storage:", storedSessions);

          // Use setState callback to get previous state instead of direct state access
          setTitrationTherapiesList((prevList) => {
            const currentList = prevList || [];

            // Create a Map for efficient duplicate detection using refId
            const sessionMap = new Map<string, TitrationSession>();

            // First, add stored sessions (they have priority and are locally available)
            storedSessions.forEach((stored) => {
              sessionMap.set(stored.session.refId, {
                ...stored.session,
                locallyAvailable: true,
              });
            });

            // Get set of stored refIds for checking locallyAvailable flag
            const storedRefIds = new Set(
              storedSessions.map((stored) => stored.session.refId)
            );

            // console.log("Stored Ref IDs:", storedRefIds);

            // Only apply filtering if we have 5 or more stored sessions
            // This indicates storage (and likely device) capacity has been reached
            const shouldFilter = storedSessions.length >= 5;
            let smallestStoredRefId = 0;

            if (shouldFilter) {
              // Find the smallest refId among stored sessions
              const storedRefIdNumbers = storedSessions
                .map((stored) => parseInt(stored.session.refId, 10))
                .filter((num) => !isNaN(num));
              smallestStoredRefId =
                storedRefIdNumbers.length > 0
                  ? Math.min(...storedRefIdNumbers)
                  : 0;

              // console.log("Smallest stored refId:", smallestStoredRefId);
            }

            // Then add sessions from current list, only if refId doesn't exist
            // Apply filtering only when we have reached storage capacity (5+ sessions)
            currentList.forEach((existingSession) => {
              if (!sessionMap.has(existingSession.refId)) {
                if (shouldFilter) {
                  const sessionRefIdNum = parseInt(existingSession.refId, 10);
                  const isValidRefId =
                    !isNaN(sessionRefIdNum) &&
                    sessionRefIdNum >= smallestStoredRefId;

                  if (isValidRefId) {
                    sessionMap.set(existingSession.refId, {
                      ...existingSession,
                      locallyAvailable: storedRefIds.has(existingSession.refId),
                    });
                  } else {
                    // console.log(
                    //   `Filtering out session with refId ${existingSession.refId} (below smallest stored refId ${smallestStoredRefId})`
                    // );
                  }
                } else {
                  // No filtering - keep all sessions when storage isn't full
                  sessionMap.set(existingSession.refId, {
                    ...existingSession,
                    locallyAvailable: storedRefIds.has(existingSession.refId),
                  });
                }
              }
            });

            // console.log("Consolidated Session Map:", sessionMap);

            // Convert Map values to array and sort by refId descending
            const consolidatedSessions = Array.from(sessionMap.values()).sort(
              (a, b) =>
                b.refId.localeCompare(a.refId, undefined, { numeric: true })
            );

            // console.log(
            //   "Final Consolidated Sessions Array:",
            //   consolidatedSessions
            // );
            return consolidatedSessions;
          });
        } catch (error) {
          console.error("Error storing titration session:", error);

          // Fallback: just update the current session as locally available if storage fails
          setTitrationTherapiesList((prev) => {
            const updatedList = prev.map((session) =>
              session.refId ===
              (currentTitrationSessionRef.current?.refId ?? "")
                ? { ...session, locallyAvailable: false } // Assume not available if storage failed
                : session
            );
            return updatedList;
          });
        }
      } else {
        // No storage available - just update the current session in the list
        setTitrationTherapiesList((prev) => {
          const updatedList = prev.map((session) =>
            session.refId === (currentTitrationSessionRef.current?.refId ?? "")
              ? { ...session, locallyAvailable: false } // Not stored locally
              : session
          );
          return updatedList;
        });
      }

      receivedTitrationChunks = []; // Clear the chunks array for the next message
    } else if (msgType === MessageType.UPDATED_TITRATION_SUMMARY) {
      const merged = mergeChunks(receivedUpdatedTitrationChunks);
      const titrationList = decodeUpdatedTitrationSummary(merged);
      console.log("Updated Titration data received:", titrationList);

      const TitrationListFormatted = titrationList.map((therapy) => {
        const refId = therapy[0].toString();
        const dateObj = new Date(therapy[1]);
        const date = dateObj.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const time = dateObj.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

        return {
          date: date,
          time: time,
          refId: refId,
          locallyAvailable: false, // Will be updated if stored locally
        };
      });

      if (storageRef.current) {
        const storedSessions = await getAllTitrationSessions(
          storageRef.current
        );

        // Create a Map for efficient duplicate detection using refId
        const sessionMap = new Map<string, TitrationSession>();

        // First, add stored sessions (they have priority)
        storedSessions.forEach((stored) => {
          sessionMap.set(stored.session.refId, {
            ...stored.session,
            locallyAvailable: true,
          });
        });
        // console.log("Stored Sessions:", storedSessions);
        // Get set of stored refIds for checking locallyAvailable flag
        const storedRefIds = new Set(
          storedSessions.map((stored) => stored.session.refId)
        );
        // console.log("Stored Ref IDs:", storedRefIds);
        // Then add new sessions from device, only if refId doesn't exist
        TitrationListFormatted.forEach((newSession) => {
          if (!sessionMap.has(newSession.refId)) {
            sessionMap.set(newSession.refId, {
              ...newSession,
              locallyAvailable: storedRefIds.has(newSession.refId),
            });
          }
        });
        // console.log("Merged Session Map:", TitrationListFormatted);
        // Convert Map values to array and sort by refId descending
        const mergedSessions = Array.from(sessionMap.values()).sort((a, b) =>
          b.refId.localeCompare(a.refId, undefined, { numeric: true })
        );
        // console.log("Merged Sessions Array:", mergedSessions);
        setTitrationTherapiesList(mergedSessions);
      } else {
        // If no storage available, just use the formatted list sorted by refId desc
        const sortedSessions = TitrationListFormatted.sort((a, b) =>
          b.refId.localeCompare(a.refId, undefined, { numeric: true })
        );
        setTitrationTherapiesList(sortedSessions);
      }
      // if (isConnectedRef.current) {
      //   setTitrationData(null);
      // }
      receivedUpdatedTitrationChunks = [];
    } else if (msgType === MessageType.UPDATED_CURVES_LIST) {
      const merged = mergeChunks(receivedUpdatedCurvesListChunks);
      const curvesList = decodeUpdatedCurvesList(merged);
      // console.log("Curves data received:", curvesList);

      // Format the received curves data
      const curvesListFormatted = curvesList.map((curve) => {
        const refId = curve[0].toString();
        const dateObj = new Date(curve[1]);
        const count = curve[2];

        const date = dateObj.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

        const time = dateObj.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

        return {
          date: date,
          time: time,
          refId: refId,
          count: count,
          locallyAvailable: false, // Will be updated if stored locally
        };
      });

      if (storageRef.current) {
        const storedCurvesSessions = await getAllCurveAnalysisSessions(
          storageRef.current
        );

        // Create a Map for efficient duplicate detection using refId
        const sessionMap = new Map<string, TherapySession>();

        // First, add stored sessions (they have priority)
        storedCurvesSessions.forEach((stored) => {
          sessionMap.set(stored.therapySession.refId, {
            ...stored.therapySession,
            locallyAvailable: true,
          });
        });

        // console.log("Stored Curve Sessions:", storedCurvesSessions);

        // Get set of stored refIds for checking locallyAvailable flag
        const storedRefIds = new Set(
          storedCurvesSessions.map((stored) => stored.therapySession.refId)
        );
        // console.log("Stored Curve Ref IDs:", storedRefIds);

        // Then add new sessions from device, only if refId doesn't exist
        curvesListFormatted.forEach((newSession) => {
          if (!sessionMap.has(newSession.refId)) {
            sessionMap.set(newSession.refId, {
              ...newSession,
              locallyAvailable: storedRefIds.has(newSession.refId),
            });
          }
        });

        // console.log("Merged Curve Session Map:", sessionMap);

        // Convert Map values to array and sort by refId descending
        const mergedSessions = Array.from(sessionMap.values()).sort((a, b) =>
          b.refId.localeCompare(a.refId, undefined, { numeric: true })
        );

        // console.log("Merged Curves Sessions Array:", mergedSessions);
        setCurvesList(mergedSessions);
      } else {
        // If no storage available, just use the formatted list
        console.log("Formatted Curves List (no storage):", curvesListFormatted);
        setCurvesList(curvesListFormatted);
      }

      receivedUpdatedCurvesListChunks = [];
    } else if (msgType === MessageType.CURVES && includedMeasurements) {
      const merged = mergeChunks(receivedCurveChunks); // merge Uint8Arrays
      const curveData = decodeCurveData(merged, includedMeasurements);
      console.log("Curve data received:", curveData.volumeData[0]);
      setCurveData(curveData);
      setCurveLoading(false);
      stopInsightsProgressTimer("curves");

      // Store the session first, then update the list based on what's actually stored
      if (
        storageRef.current &&
        deviceNameRef.current &&
        currentCurveSessionRef.current
      ) {
        try {
          // console.log("Storing curve session:", currentCurveSessionRef.current);

          // Store the curve analysis session first
          await storeCurveAnalysisSession(
            storageRef.current,
            currentCurveSessionRef.current,
            curveData,
            parseDeviceString(deviceNameRef.current).serialNumber
          );

          // After storing, fetch all available curve sessions to get accurate state
          const storedCurveSessions = await getAllCurveAnalysisSessions(
            storageRef.current
          );

          // console.log(
          //   "Stored Curve Sessions after storage:",
          //   storedCurveSessions
          // );

          // Use setState callback to get previous state instead of direct state access
          setCurvesList((prevList) => {
            const currentList = prevList || [];

            // Create a Map for efficient duplicate detection using refId
            const sessionMap = new Map<string, TherapySession>();

            // First, add stored sessions (they have priority and are locally available)
            storedCurveSessions.forEach((stored) => {
              sessionMap.set(stored.therapySession.refId, {
                ...stored.therapySession,
                locallyAvailable: true,
              });
            });

            // Get set of stored refIds for checking locallyAvailable flag
            const storedRefIds = new Set(
              storedCurveSessions.map((stored) => stored.therapySession.refId)
            );

            // console.log("Stored Curve Ref IDs:", storedRefIds);

            // Only apply filtering if we have 5 or more stored sessions
            // This indicates storage (and likely device) capacity has been reached
            const shouldFilter = storedCurveSessions.length >= 5;
            let smallestStoredRefId = 0;

            if (shouldFilter) {
              // Find the smallest refId among stored curve sessions
              const storedRefIdNumbers = storedCurveSessions
                .map((stored) => parseInt(stored.therapySession.refId, 10))
                .filter((num) => !isNaN(num));
              smallestStoredRefId =
                storedRefIdNumbers.length > 0
                  ? Math.min(...storedRefIdNumbers)
                  : 0;

              // console.log("Smallest stored curve refId:", smallestStoredRefId);
            }

            // Then add sessions from current list, only if refId doesn't exist
            // Apply filtering only when we have reached storage capacity (5+ sessions)
            currentList.forEach((existingSession) => {
              if (!sessionMap.has(existingSession.refId)) {
                if (shouldFilter) {
                  const sessionRefIdNum = parseInt(existingSession.refId, 10);
                  const isValidRefId =
                    !isNaN(sessionRefIdNum) &&
                    sessionRefIdNum >= smallestStoredRefId;

                  if (isValidRefId) {
                    sessionMap.set(existingSession.refId, {
                      ...existingSession,
                      locallyAvailable: storedRefIds.has(existingSession.refId),
                    });
                  } else {
                    // console.log(
                    //   `Filtering out curve session with refId ${existingSession.refId} (below smallest stored refId ${smallestStoredRefId})`
                    // );
                  }
                } else {
                  // No filtering - keep all sessions when storage isn't full
                  sessionMap.set(existingSession.refId, {
                    ...existingSession,
                    locallyAvailable: storedRefIds.has(existingSession.refId),
                  });
                }
              }
            });

            // console.log("Consolidated Curve Session Map:", sessionMap);

            // Convert Map values to array and sort by refId descending
            const consolidatedSessions = Array.from(sessionMap.values()).sort(
              (a, b) =>
                b.refId.localeCompare(a.refId, undefined, { numeric: true })
            );

            // console.log(
            //   "Final Consolidated Curve Sessions Array:",
            //   consolidatedSessions
            // );
            return consolidatedSessions;
          });
        } catch (error) {
          console.error("Error storing curve session:", error);

          // Fallback: just update the current session as not locally available if storage fails
          setCurvesList((prev) => {
            const updatedList = prev.map((session) =>
              session.refId === (currentCurveSessionRef.current?.refId ?? "")
                ? { ...session, locallyAvailable: false } // Assume not available if storage failed
                : session
            );
            return updatedList;
          });
        }
      } else {
        // No storage available - just update the current session in the list
        setCurvesList((prev) => {
          const updatedList = prev.map((session) =>
            session.refId === (currentCurveSessionRef.current?.refId ?? "")
              ? { ...session, locallyAvailable: false } // Not stored locally
              : session
          );
          return updatedList;
        });
      }

      receivedCurveChunks = []; // Clear the chunks array for the next message
    } else if (msgType === MessageType.LOGS && event_id) {
      // console.log('receivedLogsChunks', receivedLogsChunks);

      const merged = mergeChunks(receivedLogsChunks); // merge Uint8Arrays
      const reconstructedLogStringData = await createLogStringFromByteArray(
        merged,
        mainMap1,
        valMap1,
        storageRef.current,
        event_id.toString(),
        refId ? refId.toString() : "0"
      );
      if (reconstructedLogStringData) {
        logStringList.push(reconstructedLogStringData);
        // console.log(
        //   "reconstructed log string data",
        //   reconstructedLogStringData
        // );
        // console.log(`Log added to queue. Queue size: ${logStringList.length}`);
        setBLELogCount((prevCount) => {
          // console.log("BLELogCount setter called", prevCount);
          return prevCount + 1;
        });
        // let recentLog = await getTherapies(storage);
        setRecentLog({
          therapy: reconstructedLogStringData.reconstructedLogString,
          batch_id: reconstructedLogStringData.batch_id,
        });
        const currentBatchesMetaData = await getBatchesMetadata(storage);
        // console.log("current batch meta", currentBatchesMetaData);
        setBatchesMetaData(currentBatchesMetaData);
      } else {
        console.error("something went wrong while parsing log: ", event_id);
      }

      // await writeSendBuffMessageOverBLE();

      // Process immediately if 10 or more logs, otherwise schedule timeout
      if (logStringList.length >= 10) {
        processQueue();
      } else {
        scheduleProcessing();
      }
      receivedLogsChunks = [];
    } else if (msgType === MessageType.REALTIME_CURVES) {
      if (payload && includedMeasurements) {
        let pressure = false;
        let flow = false;
        let volume = false;
        const includedMeasurementsString =
          decodeMeasurementBitmask(includedMeasurements);
        if (includedMeasurementsString.includes("PRESSURE")) pressure = true;
        if (includedMeasurementsString.includes("FLOW")) flow = true;
        if (includedMeasurementsString.includes("VOLUME")) volume = true;
        const decodedPoints = decodeAndValidatePayload(
          payload,
          pressure,
          flow,
          volume
        );
        // console.log("Real-time curve data received:", decodedPoints);
        const { flowData, pressureData, volumeData } =
          convertToRealtimeData(decodedPoints);
        setRealtimeDataPoints({ flowData, pressureData, volumeData });
        setRealtimeData((prevData: RealtimeData) => {
          const newFlowData = [...prevData.flowData, ...flowData];
          const newPressureData = [...prevData.pressureData, ...pressureData];
          const newVolumeData = [...prevData.volumeData, ...volumeData];

          return {
            flowData: newFlowData,
            pressureData: newPressureData,
            volumeData: newVolumeData,
          };
        });
      } else {
        console.error("No payload provided for real-time curves");
      }
    }
  }

  const handlePLXDataReceived = useCallback(
    (measurement: PLXMeasurement) => {
      // console.log("📈 Setting pulse oximeter data:", measurement);
      setPulseOximeterData({
        ...measurement,
        lastReading: new Date(),
      });

      if (isTherapyRunningRef.current && therapySessionStartTimeRef.current) {
        const elapsedTime = getTherapyElapsedTime();

        // console.log("PLX reading during therapy session:", {
        //   spO2: measurement.spO2,
        //   pulseRate: measurement.pulseRate,
        //   elapsedTime: `${(elapsedTime / 1000).toFixed(1)}s`,
        //   timestamp: new Date(),
        // });

        // Store data points for graphing (only if values are valid)
        if (measurement.spO2 !== null || measurement.pulseRate !== null) {
          setPulseOximeterSessionDataPoint({
            spO2Data:
              measurement.spO2 !== null
                ? { time: elapsedTime, value: measurement.spO2 }
                : null,
            pulseRateData:
              measurement.pulseRate !== null
                ? { time: elapsedTime, value: measurement.pulseRate }
                : null,
          });
          setPulseOximeterSessionData((prevData) => {
            const newData = { ...prevData };

            // Add SpO2 data point if available
            if (measurement.spO2 !== null) {
              newData.spO2Data = [
                ...prevData.spO2Data,
                {
                  time: elapsedTime,
                  value: measurement.spO2,
                },
              ];
            }

            // Add Pulse Rate data point if available
            if (measurement.pulseRate !== null) {
              newData.pulseRateData = [
                ...prevData.pulseRateData,
                {
                  time: elapsedTime,
                  value: measurement.pulseRate,
                },
              ];
            }

            console.log(
              `PLX session data updated - SpO2 points: ${newData.spO2Data.length}, Pulse points: ${newData.pulseRateData.length}`
            );
            return newData;
          });
        }
      }
    },
    [isTherapyRunning, getTherapyElapsedTime]
  );

  // Updated priority system - focus on communication, not connection blocking
  const ensureTherapyDevicePriority = async () => {
    // If both devices are connected, therapy device operations take precedence
    if (isConnected && isPulseOximeterConnected) {
      console.log(
        "Both devices connected - therapy device has communication priority"
      );
      // This could be used to queue or delay PLX operations when therapy operations are active
    }
  };

  const connectToPulseOximeterDevice = async (
    device: ScanResult,
    deviceToDisconnectFrom?: string
  ): Promise<{ status: number; message: string }> => {
    try {
      console.log(
        "Attempting to connect to pulse oximeter:",
        device.device.name
      );

      // Handle existing PLX connection
      if (
        deviceToDisconnectFrom &&
        deviceToDisconnectFrom !== device.device.deviceId
      ) {
        console.log(
          "Disconnecting from existing pulse oximeter:",
          deviceToDisconnectFrom
        );
        await disconnectFromPulseOximeterDevice(deviceToDisconnectFrom);
      } else if (
        deviceToDisconnectFrom &&
        deviceToDisconnectFrom === device.device.deviceId
      ) {
        console.log("Already connected to this pulse oximeter");
        return { status: 1, message: "Already connected" };
      }
      setIsPulseOximeterConnectClicked({ device: device, status: true });

      // Use existing PLX connection function - no therapy device dependency
      const result = await connectToPLXDeviceSimple(
        device,
        deviceToDisconnectFrom,
        handlePLXDataReceived
      );

      if (result.status === 1) {
        console.log("Successfully connected to pulse oximeter");
        setIsPulseOximeterConnectClicked({ device: device, status: false });
        setPulseOximeterConnectionStatus(true, device);
        await savePulseOximeterToStorage(
          storage,
          device.device.deviceId,
          device.device.name ?? "Pulse Oximeter"
        );
        lastPulseOximeterManuallyDisconnectedFromRef.current = null;
      }

      return result;
    } catch (error) {
      console.error("Failed to connect to pulse oximeter:", error);
      const bleError = error as BleError;
      setIsPulseOximeterConnectClicked({ device: device, status: false });
      if (
        bleError?.errorMessage?.includes("Peer removed pairing information")
      ) {
        return {
          status: 2,
          message:
            "Peer removed pairing information! Please forget the Device from Settings and retry connecting to the Device.",
        };
      }
      lastPulseOximeterManuallyDisconnectedFromRef.current =
        device.device.deviceId;
      return {
        status: 0,
        message:
          bleError?.errorMessage ||
          (error as Error)?.message ||
          "Failed to connect to pulse oximeter",
      };
    }
  };

  const connectToDevice = async (
    device: ScanResult,
    deviceToDisconnectFrom?: string
  ) => {
    // console.log(
    //   "request to connect to device",
    //   device,
    //   "deviceToDisconnectFrom",
    //   deviceToDisconnectFrom
    // );
    try {
      setIsConnectClicked({ device: device, status: true });
      if (
        isConnected &&
        deviceToDisconnectFrom &&
        deviceToDisconnectFrom !== device.device.deviceId
      ) {
        setHiddenDisconnect(true);
        await disConnectFromDevice(deviceToDisconnectFrom);
      } else if (
        isConnected &&
        deviceToDisconnectFrom &&
        deviceToDisconnectFrom === device.device.deviceId
      ) {
        console.log("already connected");
      }

      await BleClient.connect(
        device.device.deviceId,
        (deviceId) => onDisconnect(deviceId),
        { timeout: 20000 }
      );
      if (isPlatform("android")) {
        const isBonded = await BleClient.isBonded(device.device.deviceId);
        if (!isBonded) {
          await BleClient.createBond(device.device.deviceId, {
            timeout: 60000,
          });
        }
        // Android key exchange may take time in varicite devices
        // so waiting for 1 seconds
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      // await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log("forcing pair by writing to encrypted characteristic");
      await retryWrite(
        device.device.deviceId,
        HEART_RATE_SERVICE,
        HEART_RATE_MEASUREMENT_CHARACTERISTIC,
        textToDataView("IMEI,861646043547270")
      );
      await retryWrite(
        device.device.deviceId,
        GLUCOSE_SERVICE,
        GLUCOSE_MEASUREMENT_CHARACTERISTIC,
        textToDataView("check_pair")
      );
      let MTU = 23; // Default MTU value
      if (Capacitor.getPlatform() !== "web") {
        MTU = await BleClient.getMtu(device.device.deviceId);
        console.log("MTU size:", MTU);
      }

      // Subscription
      await BleClient.stopNotifications(
        device.device.deviceId,
        HEART_RATE_SERVICE,
        HEART_RATE_MEASUREMENT_CHARACTERISTIC
      );
      await BleClient.stopNotifications(
        device.device.deviceId,
        GLUCOSE_SERVICE,
        GLUCOSE_MEASUREMENT_CHARACTERISTIC
      );
      await BleClient.startNotifications(
        device.device.deviceId,
        HEART_RATE_SERVICE,
        HEART_RATE_MEASUREMENT_CHARACTERISTIC,
        async (value) => {
          // Notification callback, this function is called for each data received from the device
          const data = dataViewToText(value);
          if (data.includes("profileNameManual")) {
            // setMode("Manual");21-11-21
            // console.log("received mode message", data);
            setMode("Manual"); //21-11-21
          } else if (data.includes("pofileNameAuto")) {
            console.log("Mode is auto");
            // console.log("received mode message", data);
            setMode("Auto");
          } else if (data.includes("Ribbon_statefalse")) {
            //enable the buttons so that play icon can be used
            setRibbonState(false);
          } else if (data.includes("Ribbon_statetrue")) {
            //disable the buttons so that play icon can not be used
            setRibbonState(true);
          }
          //Therapy state
          else if (data.includes("playStatefalse")) {
            setTherapyState(true);
            setIsTherapyRunning((prevState) => {
              console.log("Therapy started", prevState);
              if (prevState === false) {
                setRealtimeData({
                  flowData: [],
                  pressureData: [],
                  volumeData: [],
                });
                resetTherapySessionData();
                startTherapySessionTimer();
              }
              return true;
            });
          } else if (data.includes("playStatetrue")) {
            setTherapyState(false);
            setTime(0); //to reset the previous received data
          } else if (data.includes("sync_time")) {
            const list = data.split(","); //
            setTime(+list[1]); //unary +, to convert to number
          } else if (data.includes("pressGauge")) {
            // console.log("received gauge value", data);
            const list = data.split(","); //
            setGaugeValue(+list[1]); //unary +, to convert to number
          } else if (data.includes("MPT")) {
            const list = data.split(","); //
            setManualPauseTime(+list[1]); //unary +, to convert to number
          } else if (data.includes("MIT")) {
            const list = data.split(","); //
            setManualInspTime(+list[1]); //unary +, to convert to number
          } else if (data.includes("MET")) {
            const list = data.split(","); //
            setManualExpTime(+list[1]); //unary +, to convert to number
          } else if (data.includes("MCN")) {
            const list = data.split(","); //
            setManualCycleNumber(+list[1]); //unary +, to convert to number
          } else if (data.includes("userDisconnected")) {
            lastDeviceManuallyDisconnectedFromRef.current = deviceIdRef.current;
          } else if (data.includes("profileName")) {
            const list = data.split(","); //
            if (list[1]?.startsWith("A")) {
              setMode("Auto");
            } else if (list[1]?.startsWith("M")) {
              setMode("Manual");
            } else {
              setMode("default");
            }
            setCurrentProfileName(list[1]); //unary +, to convert to number
          } else if (data.includes("DeviceType")) {
            const list = data.split(",");
            // setDeviceType(list[1]?.split(":")[0] as "Cough" | "Clear");
            // setSerialNumber(list[1]?.split(":")[1]);
            setDeviceVersion(list[2]);
          } else if (data.includes("therapyComplete")) {
            setTimeout(() => {
              setIsTherapyRunning(false);
            }, 2000);
            const list = data.split(",");
            // console.log("therapy complete received", data, list);
            const refId = list[1];
            const dateObj = new Date(list[2]);
            const date = dateObj.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });

            // Format time: "14:30"
            const time = dateObj.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false, // Change to true for 12-hour format
            });
            setTimeout(() => {
              // setRealtimeData({
              //   flowData: [],
              //   pressureData: [],
              //   volumeData: [],
              // });
              setRealtimeDataPoints({
                flowData: [],
                pressureData: [],
                volumeData: [],
              });
              setPulseOximeterSessionDataPoint({
                spO2Data: null,
                pulseRateData: null,
              });
            }, 2000);

            realtimeDataCountRef.current = 0; // Reset count after storing
            setGaugeValue(0); // Reset gauge value after therapy complete
          }
          // Therapy parameter message 1 (timing parameters)
          else if (data.startsWith("TP1+")) {
            // Parse therapy parameters part 1
            // console.log("tp1 received", data);
            const paramsPart = data.split("+")[1];
            //Message Syntax TP1 : indicate which tp message + : indicator separator  , : values separator
            const [
              enabledPhasesHex,
              pauseDurationDS,
              inspDurationDS,
              expDurationDS,
              inspTriggerST,
              cycleNM,
            ] = paramsPart.split(",");

            // Parse enabled phases from hex
            const enabledFlags = parseInt(enabledPhasesHex, 16); //list of enabled phases as a 3 bit binary
            const enabledPhases = [];
            if (enabledFlags & 0x01) enabledPhases.push(TherapyPhase.PAUSE);
            if (enabledFlags & 0x02)
              enabledPhases.push(TherapyPhase.INSPIRATORY);
            if (enabledFlags & 0x04)
              enabledPhases.push(TherapyPhase.EXPIRATORY);

            // Convert deciseconds to milliseconds
            const pauseDuration = parseInt(pauseDurationDS) * 100; //pause phase duration
            const inspDuration = parseInt(inspDurationDS) * 100; //insp phase duration
            const expDuration = parseInt(expDurationDS) * 100; // exp phase duration
            const inspTriggerStatus = parseInt(inspTriggerST); // insp trigger status
            const currentCycleNumber = parseInt(cycleNM); // current cycle number
            // console.log(
            //   "tp1 extracted",
            //   "enabledPhases: ",
            //   enabledPhases,
            //   "pauseDuration: ",
            //   pauseDuration,
            //   "inspDuration: ",
            //   inspDuration,
            //   "inspTriggerStatus: ",
            //   inspTriggerStatus,
            //   "cycleNumber: ",
            //   currentCycleNumber
            // );

            // Store therapy parameters part 1 directly in the ref
            therapyParamsPart1Ref.current = {
              enabledPhases,
              pauseDuration,
              inspDuration,
              expDuration,
              inspTriggerStatus,
              currentCycleNumber,
            };

            // If we already have part 2, update complete therapy parameters
            // if (therapyParamsPart2Ref.current) {
            //   updateCompleteTherapyParams();
            // }
          }
          // Therapy parameter message 2 (pressure parameters)
          else if (data.startsWith("TP2+")) {
            // console.log("tp2 received", data);
            // Parse therapy parameters part 2
            const paramsPart = data.split("+")[1];
            const [
              pausePressure,
              inspPressure,
              expPressure,
              totalCycles,
              oscStatus,
            ] = paramsPart.split(",").map((val) => parseInt(val));
            // console.log(
            //   "tp1 extracted",
            //   "pausePressure: ",
            //   pausePressure,
            //   "inspPressure: ",
            //   inspPressure,
            //   "totalCycles: ",
            //   totalCycles,
            //   "oscStatus: ",
            //   oscStatus
            // );

            // Store therapy parameters part 2 directly in the ref
            therapyParamsPart2Ref.current = {
              pausePressure,
              inspPressure,
              expPressure,
              oscStatus,
              totalCycles,
            };

            // If we already have part 1, update complete therapy parameters
            if (therapyParamsPart1Ref.current) {
              updateCompleteTherapyParams();
            }
          }
          // Phase Change message
          else if (data.startsWith("PC+")) {
            // Parse phase change data
            // console.log("phase change data received", data);
            const phasePart = data.split("+")[1];
            const [phaseCode, progressValue] = phasePart
              .split(",")
              .map((val) => parseInt(val));

            // Update phase and progress
            setCurrentPhase(decodePhase(phaseCode));
            setPhaseProgress(progressValue / 100); // Convert 0-99 to 0-0.99
          }
          // // initial state from device
          // else if(data.includes('PEPpressed')){console.error('PEP DATA');
          //   setPepState(true);
          //   setRunningState(prev=>{
          //     return {
          //       // previous:prev.current,
          //       // previous:running?'paused':'running',
          //       // current:running?'running':'paused',
          //       previous:'paused',
          //       current:'running',
          //     }
          //   })
          // }
          // else if(data.includes('PEPreleased')){
          //   setPepState(false);
          // }
          // else if(data.includes('OSCpressed')){console.error('OSC DATA');
          //   setOscState(true);
          //   setRunningState(prev=>{
          //     return {
          //       // previous:prev.current,
          //       // previous:running?'paused':'running',
          //       // current:running?'running':'paused'
          //       previous:'paused',
          //       current:'running',
          //     }
          //   })
          // }
          // else if(data.includes('OSCreleased')){
          //   setOscState(false);
          // }
          // else if(data.includes('NEBpressed')){console.error('NEB DATA');
          //   setNebState(true);
          //   setRunningState(prev=>{
          //     return {
          //       // previous:prev.current,
          //       // previous:running?'paused':'running',
          //       // current:running?'running':'paused'
          //       previous:'paused',
          //       current:'running',
          //     }
          //   })
          // }
          // else if(data.includes('NEBreleased')){
          //   setNebState(false);
          // }
          else {
            console.error(
              "Uncaught data11111111111111111111111111111111111: ",
              data
            );
            setHeartRatedata(data);
          }
          // console.error("Data from server: "+"'"+data+"'");
        }
      ).then(
        () => {
          console.log("Start Notification: PASS HR");
        },
        () => {
          console.error("Start Notification fail");
        }
      );

      await BleClient.startNotifications(
        device.device.deviceId,
        GLUCOSE_SERVICE,
        GLUCOSE_MEASUREMENT_CHARACTERISTIC,
        async (value) => {
          // Notification callback, this function is called for each data received from the device
          // const data = dataViewToText(value);
          // console.log(
          //   "Converted text data from GLUCOSE_MEASUREMENT_CHARACTERISTIC:",
          //   data
          // );
          const data1 = dataViewToNumbers(value);

          await handleGlucoseMessage(data1);
          // console.error("Data from server: "+"'"+data+"'");
        }
      ).then(
        () => {
          console.log(
            "Start Notification GLUCOSE_MEASUREMENT_CHARACTERISTIC: PASS"
          );
        },
        () => {
          console.error(
            "Start Notification fail GLUCOSE_MEASUREMENT_CHARACTERISTIC"
          );
        }
      );
      console.log("Subscribed to Notifications");
      // //   make subscription end

      const savedDevice = await loadDeviceFromStorage(storage);

      if (
        savedDevice &&
        savedDevice.deviceId !== device.device.deviceId &&
        storageRef.current
      ) {
        await clearTherapyData(storageRef.current);
        await clearCurveAnalysisData(storageRef.current);
        await clearAdherenceData(storageRef.current);
        await clearAllTitrationSessions(storageRef.current);
        //Clearing previous insights Data:
        setAdherenceData(null);
        setTitrationData(null);
        setCurveData(null);
        setTitrationTherapiesList([]);
        setCurvesList([]);
      }

      await BleClient.write(
        device.device.deviceId,
        HEART_RATE_SERVICE,
        HEART_RATE_MEASUREMENT_CHARACTERISTIC,
        textToDataView(`MTU,${MTU}`)
      );

      await BleClient.write(
        device.device.deviceId,
        HEART_RATE_SERVICE,
        HEART_RATE_MEASUREMENT_CHARACTERISTIC,
        textToDataView(
          `Name,${localDeviceName ? localDeviceName : "Unknown Device"}`
        )
      );

      await BleClient.write(
        device.device.deviceId,
        HEART_RATE_SERVICE,
        HEART_RATE_MEASUREMENT_CHARACTERISTIC,
        textToDataView(`MaxTherapiesList,5`)
      );

      await askForSelectiveData(
        [RequestMessageType.MAPS_FOR_LOGS, RequestMessageType.LOGS],
        device
      );
      await askForMaps(device);

      setConnectionStatus(true, device);
      setShowNewDeviceModal(false);
      setDevices((prevDevices) => {
        const exists = prevDevices.findIndex(
          (d) => d.device.deviceId === device.device.deviceId
        );
        if (exists === -1) {
          if (prevDevices.length === 0) {
            setNewDeviceIndex(0);
          }
          return [...prevDevices, device];
        } else {
          const connectedDevice = prevDevices.splice(exists, 1)[0];
          return [connectedDevice, ...prevDevices];
        }
      });

      // console.log("saved device check on connect", savedDevice);

      if (device.device.deviceId && device.device.name) {
        const { deviceType, serialNumber } = parseDeviceString(
          device.device.name
        );
        saveDeviceToStorage(
          storage,
          device.device.deviceId,
          device.device.name,
          deviceType,
          serialNumber
        );
        if (deviceType && serialNumber) {
          setDeviceType(deviceType);
          setSerialNumber(serialNumber);
        }
      }
      lastDeviceManuallyDisconnectedFromRef.current = null; // Reset last manually disconnected device
      setIsConnectClicked({ device: device, status: false });
      logUserAction(
        "successful_device_connection by user to device " + device.device.name
      );
      await analytics.logGenericEvent("BLE_DEVICE_CONNECTED", {
        deviceId: device.device.deviceId,
        deviceName: device.device.name || "Unknown",
      });
      return { status: 1, message: "Connected to device successfully" }; // Successfully connected
      // Scanning continues in the background
    } catch (error) {
      logUserAction(
        "failed_device_connection by user to device " + device.device.name
      );
      await analytics.logGenericEvent("BLE_DEVICE_CONNECT_FAILED", {
        deviceId: device.device.deviceId,
        deviceName: device.device.name || "Unknown",
      });
      setIsConnectClicked({ device: device, status: false });
      console.error("Failed to connect to device:", error);
      // Cast error to BleError type to properly access errorMessage property
      const bleError = error as BleError;
      if (isPlatform("android")) {
        const errorMessage = (error as Error)?.message || "";
        console.log("Android error message:", errorMessage);
        if (errorMessage.includes("201")) {
          console.log(
            "Could be Android Bonding issue,reset bond and try again"
          );
          openModal(
            "connectFailed",
            device,
            "Peer removed pairing information! Please forget the Device from Settings and retry connecting to the Device."
          );
          return {
            status: 2,
            message:
              "Peer removed pairing information! Please forget the Device from Settings and retry connecting to the Device.",
          };
        } else {
          return {
            status: 0,
            message: errorMessage || "Failed to connect to device",
          };
        }
      } else {
        if (
          bleError?.errorMessage?.includes("Peer removed pairing information")
        ) {
          lastDeviceManuallyDisconnectedFromRef.current =
            device.device.deviceId;
          openModal(
            "connectFailed",
            device,
            "Peer removed pairing information! Please forget the Device from Settings and retry connecting to the Device."
          );
          return {
            status: 2,
            message:
              "Peer removed pairing information! Please forget the Device from Settings and retry connecting to the Device.",
          };
        }
        // await startScanning();
        return {
          status: 0,
          message:
            bleError?.errorMessage ||
            (error as Error)?.message ||
            "Failed to connect to device",
        };
      }
    }
  };

  const reconnectToSavedDevice = async () => {
    const savedDevice = await loadDeviceFromStorage(storage);
    if (savedDevice) {
      try {
        const prevDevice = await BleClient.getDevices([savedDevice.deviceId]); // For iOS to connect to prevConnected dev without scan
        await connectToDevice({ device: prevDevice[0] });
        setShowSplash(false);
        startScanningCycle();
      } catch (error) {
        console.error("Failed to reconnect to saved device:", error);
        setShowSplash(false);
        startScanningCycle();
      }
    } else {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setShowSplash(false);
      startScanningCycle();
    }
  };

  const findAndConnectToDeviceWeb = async () => {
    try {
      const selectedDevice = await BleClient.requestDevice({
        services: [HEART_RATE_SERVICE, GLUCOSE_SERVICE, PULSE_OXIMETER_SERVICE],
      });
      console.log("Selected device:", selectedDevice);
      if (selectedDevice && !selectedDevice.name?.includes("Nonin")) {
        await connectToDevice({ device: selectedDevice });
      } else if (selectedDevice && selectedDevice.name?.includes("Nonin")) {
        await connectToPulseOximeterDevice({ device: selectedDevice });
      }
    } catch (error) {
      console.error("Error selecting device:", error);
    }
  };

  const disConnectFromDevice = async (deviceId: string) => {
    try {
      lastDeviceManuallyDisconnectedFromRef.current = deviceId;
      await BleClient.disconnect(deviceId);
    } catch (error) {
      console.error("Failed to disconnect to device:", error);
    }
  };

  const disconnectFromPulseOximeterDevice = async (deviceId: string) => {
    try {
      lastPulseOximeterManuallyDisconnectedFromRef.current = deviceId;
      console.log("Disconnecting from pulse oximeter:", deviceId);
      await BleClient.disconnect(deviceId);
      setPulseOximeterConnectionStatus(false, null);
      setPulseOximeterData(null);

      // Remove from devices list
      setPulseOximeterDevices((prevDevices) =>
        prevDevices.filter((d) => d.device.deviceId !== deviceId)
      );

      console.log("Successfully disconnected from pulse oximeter");
    } catch (error) {
      console.error("Failed to disconnect from pulse oximeter:", error);
    }
  };

  const dismissDevicePopup = async () => {
    setShowNewDeviceModal(false);
    setNewDeviceIndex(-1);
  };

  const showNextDevice = () => {
    setNewDeviceIndex((prevIndex) =>
      prevIndex + 1 < devices.length ? prevIndex + 1 : 0
    );
  };

  const handleSyncAdherenceSummary = async (dateRange: {
    start: string;
    end: string;
    tz: string;
  }) => {
    console.log(
      "Syncing adherence summary for date range:",
      dateRange,
      `sendAdherence,${dateRange.start},${dateRange.end},${dateRange.tz}`
    );
    if (!isConnected) {
      openModal("notConnectedToDevice", null);
      return;
    } else {
      if (titrationLoading) {
        alert(
          "Please wait for titration sync to complete before syncing adherence."
        );
        return;
      }
      if (curveLoading) {
        alert(
          "Please wait for curve analysis sync to complete before syncing adherence."
        );
        return;
      }
      setAdherenceLoading(true);
      if (device)
        await BleClient.write(
          device.device.deviceId,
          GLUCOSE_SERVICE,
          GLUCOSE_MEASUREMENT_CHARACTERISTIC,
          textToDataView(
            `sendAdherence,${dateRange.start},${dateRange.end},${dateRange.tz}`
          )
        );
      startInsightsProgressTimer("adherence", () => {
        setAdherenceLoading(false);
        setAdherenceBackgroundLoading(false);
        setAdherenceData(null);
        setAdherenceProgress(0);
        receivedAdherenceChunks = []; // Clear the chunks array for the next message
      });
    }
  };

  const handleSyncTitration = async (session: TitrationSession) => {
    currentTitrationSessionRef.current = session; // Store the current session for later use
    if (storageRef.current) {
      try {
        // Fetch titration data from local storage when not connected
        const storedSession = await getTitrationSessionByRefId(
          storageRef.current,
          session.refId
        );

        if (storedSession) {
          console.log(
            `Loading titration data from local storage - RefId: ${session.refId}`
          );
          setTitrationData(storedSession.titrationData);
        } else {
          if (!isConnected) {
            console.log(
              `Titration session not found locally - RefId: ${session.refId}`
            );
            alert(
              "This titration session is not available offline. Please connect to the device to sync this data."
            );
          } else {
            if (adherenceLoading || adherenceBackgroundLoading) {
              alert(
                "Please wait for adherence sync to complete before syncing titration."
              );
              return;
            }
            if (curveLoading) {
              alert(
                "Please wait for curve analysis sync to complete before syncing titration."
              );
              return;
            }

            setTitrationLoading(true);
            if (device)
              await BleClient.write(
                device.device.deviceId,
                GLUCOSE_SERVICE,
                GLUCOSE_MEASUREMENT_CHARACTERISTIC,
                textToDataView("sendTitration," + session.refId)
              );
            startInsightsProgressTimer("titration", () => {
              setTitrationLoading(false);
              setTitrationData(null);
              setTitrationProgress(0);
              receivedTitrationChunks = []; // Clear the chunks array for the next message
            });
          }
        }
      } catch (error) {
        console.error(
          "Error loading titration data from local storage:",
          error
        );
        alert("Failed to load titration data from local storage.");
      }
    }
  };

  const handleSyncCurveAnalysis = async (
    session: TherapySession,
    pressure: boolean,
    flow: boolean,
    volume: boolean
  ) => {
    console.log(
      "handleSyncCurveAnalysis called with session:",
      session,
      pressure,
      flow,
      volume
    );
    // Calculate param1 using bitwise operations
    let param1 = 0;
    if (pressure) param1 |= 1 << 0; // Set bit 0 for pressure
    if (flow) param1 |= 1 << 1; // Set bit 1 for flow
    if (volume) param1 |= 1 << 2; // Set bit 2 for volume

    if (storageRef.current) {
      const storedSession = await getCurveAnalysisSessionById(
        storageRef.current,
        session.refId
      );
      currentCurveSessionRef.current = session; // Store the current session for later use
      if (storedSession) {
        setCurveData(storedSession.curveAnalysisData);
      } else {
        if (!isConnected) {
          openModal("notConnectedToDevice", null);
          return;
        } else {
          if (adherenceLoading || adherenceBackgroundLoading) {
            alert(
              "Please wait for adherence sync to complete before syncing curves."
            );
            return;
          }
          if (titrationLoading) {
            alert(
              "Please wait for titration sync to complete before syncing curves."
            );
            return;
          }
          console.log(
            "Syncing curve analysis for refId:",
            session.refId,
            "sendCurves" + "," + param1 + "," + session.refId
          );
          setCurveLoading(true);
          if (device)
            await BleClient.write(
              device.device.deviceId,
              GLUCOSE_SERVICE,
              GLUCOSE_MEASUREMENT_CHARACTERISTIC,
              textToDataView("sendCurves" + "," + param1 + "," + session.refId) // Assuming session.refId is the correct identifier
            );
          startInsightsProgressTimer("curves", () => {
            setCurveLoading(false);
            setCurveData(null);
            setCurvesProgress(0);
            receivedCurveChunks = []; // Clear the chunks array for the next message
          });
        }
      }
    }
  };

  const currentDevice = devices[newDeviceIndex] || null;

  useEffect(() => {
    const initialize = async () => {
      const storageInstance = await initStorage(storage); // Ensure storage is initialized
      setStorage(storageInstance);
      if (storageInstance) {
        const StoredAdherenceData = await getLastFetchedAdherence(
          storageInstance
        );
        console.log("Adherence data from storage:", StoredAdherenceData);
        setAdherenceData(StoredAdherenceData?.adherenceData || null);
        setStoredAdherenceDeviceId(
          StoredAdherenceData?.deviceId || "Unknown Device"
        );

        const savedTitrationSessions = await getAllTitrationSessions(
          storageInstance
        );
        console.log(
          "All saved titration sessions from storage:",
          savedTitrationSessions
        );
        // Filter sessions that are locally available and sort by refId descending

        const savedSessionsDevice = savedTitrationSessions
          .filter((session) => session.session.locallyAvailable === true)
          .sort((a, b) =>
            b.session.refId.localeCompare(a.session.refId, undefined, {
              numeric: true,
            })
          );
        console.log(
          "Locally available titration sessions init1:",
          savedSessionsDevice
        );
        const locallyAvailableTitrationTherapiesList = savedSessionsDevice.map(
          (session) => session.session
        );
        setTitrationTherapiesList(locallyAvailableTitrationTherapiesList);
        // console.log("Titration data from storage:", StoredTitrationData);
        setTitrationData(savedSessionsDevice[0]?.titrationData || null);
        setStoredTitrationDeviceId(
          savedSessionsDevice[0]?.deviceId || "Unknown Device"
        );

        const StoredCurveSessions = await getAllCurveAnalysisSessions(
          storageInstance
        );
        console.log("Curve sessions from storage:", StoredCurveSessions);

        const savedCurveSessionsDevice = StoredCurveSessions.filter(
          (session) =>
            session.therapySession.locallyAvailable === true &&
            session.deviceId === StoredAdherenceData?.deviceId
        ).sort((a, b) =>
          b.therapySession.refId.localeCompare(
            a.therapySession.refId,
            undefined,
            {
              numeric: true,
            }
          )
        );
        const savedCurvesList = savedCurveSessionsDevice.map(
          (session) => session.therapySession
        );
        console.log("Saved curves list init1:", savedCurvesList);
        setCurvesList(savedCurvesList);
        const storedCurveData = await getCurveAnalysisSessionById(
          storageInstance,
          savedCurvesList && savedCurvesList[0]?.refId
        );
        // console.log("Curve data from storage:", storedCurveData);
        setCurveData(storedCurveData?.curveAnalysisData || null);
        setStoredCurveDataDeviceId(
          storedCurveData?.deviceId || "Unknown Device"
        );
      }
    };
    const logDeviceInfo = async () => {
      const info = await LocalDevice.getInfo();

      // console.log("LOCAL DE4VICE INFO", info);
      if (info.name) setLocalDeviceName(info.name);
    };

    initialize(); // Call the initializer
    logDeviceInfo();
    return () => {
      stopScanningCycle();
      if (isConnected && device && device.device.deviceId) {
        disConnectFromDevice(device.device.deviceId);
      }
    };
  }, []);

  useEffect(() => {
    // Function to run your BLE logic
    handleBLECheck();
  }, [location.pathname]);

  // Effect for app state changes (background/foreground)
  useEffect(() => {
    let appStateListener: PluginListenerHandle;

    const setupAppStateListener = async () => {
      // Listen for app state changes
      appStateListener = await App.addListener("appStateChange", (state) => {
        // console.log("App state changed:", state);

        if (state.isActive) {
          // App came to foreground
          // console.log("App came to foreground - running BLE check");
          handleBLECheck();
        } else {
          // App went to background
          // console.log("App went to background");
        }
      });
    };

    setupAppStateListener();

    // Cleanup listener on unmount
    return () => {
      cleanup;
      if (appStateListener) {
        appStateListener.remove();
      }
    };
  }, [location.pathname, isBleInitialized]);

  useEffect(() => {
    // console.log("storage is", storage);
    // Only attempt to load from storage once `storage` has been initialized
    const getTherapyFirstTime = async (storage1: Storage) => {
      const recentLog = await getTherapies(storage1);
      // console.log("recentLog is", recentLog);
      if (recentLog) {
        setRecentLog(recentLog);
        const currentBatchesMetaData = await getBatchesMetadata(storage1);
        // console.log("current batch meta first time", currentBatchesMetaData);
        setBatchesMetaData(currentBatchesMetaData);
      }
    };
    if (storage && location.pathname.includes("/remote")) {
      // reconnectToSavedDevice(); // Try reconnecting after storage is ready
      getTherapyFirstTime(storage);
    }
  }, [storage, location.pathname]);

  useEffect(() => {
    // console.log("isConnected changed:", isConnected);
    if (
      !isConnected &&
      devices.length > 0 &&
      currentDevice &&
      !hiddenDisconnect
    ) {
      setShowNewDeviceModal(true);
    }
  }, [isConnected, devices]);

  useEffect(() => {
    // console.log("scanningStatus changed:", scanningStatus);
    // Clear any existing timeouts first
    clearScanTimeouts();
    if (scanningStatus === "scanning") {
      // Schedule the pause after SCAN_DURATION
      setTimeout(async () => {
        await pauseScanning();
      }, SCAN_DURATION);
    } else if (scanningStatus === "paused") {
      setTimeout(() => {
        startScanning();
      }, PAUSE_DURATION);
    }
    // Cleanup function
    return () => {
      clearScanTimeouts();
    };
  }, [scanningStatus]);

  // Update the ref whenever isConnected state changes
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  // Update the ref whenever isConnected state changes
  useEffect(() => {
    console.log("device changed:", device);
    if (device && device?.device && device.device.name) {
      console.log("updating device refs:", device.device);
      deviceIdRef.current = device.device.deviceId;
      deviceNameRef.current = device.device.name;
    }
  }, [device]);

  useEffect(() => {
    isConnectClickedRef.current = isConnectClicked;
  }, [isConnectClicked]);

  // Update the ref whenever isConnected state changes
  useEffect(() => {
    // console.log("realtimeData changed:", realtimeData.flowData.length);
    if (realtimeData.flowData.length > 0) {
      realtimeDataCountRef.current = realtimeData.flowData.length;
      realtimeDataRef.current = realtimeData;
    }
  }, [realtimeData]);

  // Update the ref whenever isConnected state changes
  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  useEffect(() => {
    isTherapyRunningRef.current = isTherapyRunning;
  }, [isTherapyRunning]);

  // Cleanup function for component unmount
  useEffect(() => {
    return () => {
      clearServerStatusQueue();
    };
  }, []);

  // useEffect(() => {
  //   const SendBuffIntervalId = setInterval(async () => {
  //     try {
  //       // console.log('sendBuffTimestamp',sendBuffTimestampRef.current)
  //       const elapsed = Date.now() - sendBuffTimestampRef.current;
  //       // console.log('Elapsed is', elapsed);
  //       if (elapsed > 2000) {
  //         // console.log('Calling writeSendBuffMessageOverBLE');
  //         await writeSendBuffMessageOverBLE();
  //       }
  //     } catch (error: unknown) {
  //       // Log additional details if available
  //       console.error(
  //         "Error in interval callback:",
  //         error instanceof Error ? error.message : String(error),
  //         error
  //       );
  //     }
  //   }, 1000);

  //   return () => clearInterval(SendBuffIntervalId);
  // }, []);
  // commented for reducing load on device

  // Clean up when connection state changes
  useEffect(() => {
    if (!isConnected) {
      clearServerStatusQueue();
    }
  }, [isConnected]);

  useEffect(() => {
    return () => {
      clearScanTimeouts();
      stopScanningCycle();
    };
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const checkBleState = async () => {
      if (!location.pathname.includes("remote")) {
        // Skip BLE checks on Non-remote pages
        return;
      }
      try {
        const isEnabled = await BleClient.isEnabled();
        const connectionStatus = getConnectionStatus();
        // Only act when BLE goes from enabled → disabled
        if (isEnabled === false) {
          // Device's Bluetooth has been turned off -show status appropriately
          if (connectionStatus.therapyConnected && device?.device?.deviceId) {
            await onDisconnect(device.device.deviceId);
          }
          if (
            connectionStatus.plxConnected &&
            pulseOximeterDevice?.device?.deviceId
          ) {
            await onPulseOximeterDisconnect(
              pulseOximeterDevice.device.deviceId
            );
          }
          const platform = Capacitor.getPlatform();

          if (platform === "ios") {
            alert(
              "Please enable Bluetooth to continue using the remote.\nGo to Settings → Bluetooth → Turn On"
            );
            history.push("/home");
          } else if (platform === "android") {
            try {
              await BleClient.requestEnable();
            } catch (error) {
              console.error("Error enabling BLE:", error);
              alert(
                "Unable to enable Bluetooth. Please enable it manually to continue using the remote."
              );
              history.push("/home");
            }
          }
        }
      } catch (error) {
        console.error("BLE check error:", error);
      }
    };

    interval = setInterval(
      checkBleState,
      Capacitor.getPlatform() === "ios" ? 3000 : 5000
    );
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [
    isConnected,
    isPulseOximeterConnected,
    device,
    pulseOximeterDevice,
    location.pathname,
  ]);

  // Start mock data generation when component mounts
  // useEffect(() => {
  //   const stopGenerator = createMockDataGenerator(5, (data) => {
  //     setRealtimeData(data);
  //   });
  //   stopDataGeneratorRef.current = stopGenerator;

  //   return () => {
  //     if (stopDataGeneratorRef.current) {
  //       stopDataGeneratorRef.current();
  //     }
  //   };
  // }, [setRealtimeData]);

  return (
    <BLEContext.Provider
      value={{
        isConnected,
        device,
        devices,
        newDevice: currentDevice,
        setConnectionStatus,
        connectToDevice,
        dismissDevicePopup,
        showNextDevice,
        showNewDeviceModal,
        disConnectFromDevice,
        serialNumber,
        deviceType,
        ribbonState,
        serverSuccessLogCount,
        serverFailLogCount,
        BLELogCount,
        batchesMetaData,
        mainMap,
        valMap,
        scanningStatus,
        batchCount,
        heartRateData,
        mode,
        therapyState,
        setTherapyState,
        time,
        setTime,
        HEART_RATE_SERVICE,
        HEART_RATE_MEASUREMENT_CHARACTERISTIC,
        recentLog,
        storage,
        showSplash,
        currentPhase,
        phaseProgress,
        therapyParams,
        openModal,
        closeModal,
        handleModalAction,
        toggleTherapyState,
        handleInspPhaseStart,
        handleInspPhaseStop,
        handleExpPhaseStart,
        handleExpPhaseStop,
        gaugeValue,
        manualExpTime,
        manualInspTime,
        manualPauseTime,
        currentProfileName,
        manualCycleNumber,
        handleFindDevice,
        handleSyncAdherenceSummary,
        adherenceLoading,
        adherenceData,
        handleSyncTitration,
        titrationData,
        titrationLoading,
        curveData,
        curveLoading,
        handleSyncCurveAnalysis,
        curvesList,
        curvesProgress,
        adherenceProgress,
        titrationProgress,
        storedAdherenceDeviceId,
        storedTitrationDeviceId,
        storedCurveDataDeviceId,
        realtimeData,
        realtimeDataPoints,
        askForPressureGaugeData,
        askForRealTimeCurves,
        parseDeviceString,
        askForSelectiveData,
        setCurveData,
        titrationTherapiesList,
        deviceVersion,
        isConnectClicked,
        adherenceBackgroundLoading,
        isTherapyRunning,
        connectToPLXDeviceSimple,
        isPulseOximeterConnected,
        pulseOximeterDevice,
        pulseOximeterDevices,
        pulseOximeterData,
        setPulseOximeterConnectionStatus,
        connectionStatus: getConnectionStatus(),
        DeviceType,
        getDeviceType,
        connectToPulseOximeterDevice,
        disconnectFromPulseOximeterDevice,
        handlePLXDataReceived,
        isPulseOximeterConnectClicked,
        pulseOximeterSessionData,
        pulseOximeterSessionDataPoint,
        findAndConnectToDeviceWeb,
      }}
    >
      {children}

      {modalConfig.isOpen && (
        <ActionButtonsConnected
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          title={getModalMessages().Title}
          message={getModalMessages().Message}
          actionButtons={
            Array.isArray(buttonText)
              ? buttonText.map((text, index) => ({
                  text,
                  action: async () => await handleModalAction(index),
                  isPrimary: index === 0,
                  backgroundColor: index === 0 ? "#FF0000" : undefined,
                }))
              : [
                  {
                    text: buttonText,
                    action: async () => await handleModalAction(),
                    isPrimary: true,
                    backgroundColor: "#FF0000",
                  },
                ]
          }
          isDarkMode={true}
          showDeviceInfo={true}
          deviceInfo={{
            serialNumber: serialNumber ?? "",
            deviceId: device?.device.deviceId ?? "",
            deviceType: deviceType
              ? deviceType
              : device && device.device.name
              ? parseDeviceString(device.device.name).deviceType
              : "Unknown",
          }}
          showPulseDevice={
            modalConfig.action === "pulseDeviceDisconnect" ||
            modalConfig.action === "connectedPulseOximeter"
          }
        />
      )}
    </BLEContext.Provider>
  );
};
